import { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  Users,
  UserPlus,
  Search,
  FolderPlus,
  ArrowRight,
  LogOut,
  LayoutDashboard,
  User,
  Settings,
  Share2,
  Folder,
  Mail,
  Plus,
  X,
  Globe,
  ShieldCheck,
  Layers,
  Check,
  Star,
  Pencil,
  Trash2,
  Menu,
  ChevronLeft,
  Phone,
  AtSign,
  Tag,
  Eye,
  ChevronDown,
  Filter,
  QrCode,
  Copy,
} from 'lucide-react';
// Lazy-loaded components for code splitting (heavy QR dependencies)
const QRScannerModal = lazy(() => import('./components/QRScannerModal'));
const ProfileQRCode = lazy(() => import('./components/ProfileQRCode'));
// Firebase is lazy-loaded — no static imports to keep it out of the critical chain
const generateGroupId = () => `g-${Date.now()}`;
const generateFolderId = () => `f-${Date.now()}`;

// ─── Responsive Hook ──────────────────────────────────────────────────────────
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
};

// ─── App ──────────────────────────────────────────────────────────────────────
const App = () => {
  const isMobile = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [localGroups, setLocalGroups] = useState([]);
  const [sharedFolders, setSharedFolders] = useState([]);
  // Map folderId → contacts[] pour le partage inter-utilisateurs
  const [folderContactsMap, setFolderContactsMap] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [contactSourceFilter, setContactSourceFilter] = useState('all');
  const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');
  const [selectedLabelFilter, setSelectedLabelFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);

  const [authLoading, setAuthLoading] = useState(true); // true until Firebase checks session
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [sharingGroup, setSharingGroup] = useState(null);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [viewingContact, setViewingContact] = useState(null);
  const [userProfile, setUserProfile] = useState({});
  const [expandedContactId, setExpandedContactId] = useState(null);
  const [groupShareFilter, setGroupShareFilter] = useState('all');
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [showCameraPermissionModal, setShowCameraPermissionModal] = useState(false);
  const [scannedContact, setScannedContact] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hiddenSharedContactIds, setHiddenSharedContactIds] = useState(new Set());
  // Profil partagé via lien (?share=uid)
  const [sharedProfileData, setSharedProfileData] = useState(null); // { uid, displayName, email, phone, instagram }
  const [sharedProfileLoading, setSharedProfileLoading] = useState(false);

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setSelectedGroup(null);
    setSelectedFolder(null);
    setIsSidebarOpen(false);
  }, []);

  // Refs pour les désabonnements Firestore
  const contactsUnsubRef = useRef(null);
  const groupsUnsubRef = useRef(null);
  const foldersUnsubRef = useRef(null);
  const folderContactUnsubsRef = useRef({});
  const profileUnsubRef = useRef(null);
  // Lazy-loaded Firestore functions (loaded after login)
  const dbRef = useRef(null);
  // Lazy-loaded Firebase auth functions
  const authFnsRef = useRef({ signInWithGoogle: null, signOutUser: null });

  // Observer Firebase Auth + souscriptions Firestore
  useEffect(() => {
    let cancelled = false;
    let unsubscribe = null;

    const initAuth = async () => {
      // Dynamically load Firebase Auth (keeps it out of critical rendering path)
      const [firebaseModule, authModule] = await Promise.all([
        import('./firebase'),
        import('firebase/auth'),
      ]);

      if (cancelled) return;

      // Store auth functions for later use
      authFnsRef.current.signInWithGoogle = firebaseModule.signInWithGoogle;
      authFnsRef.current.signOutUser = firebaseModule.signOutUser;

      // Safari : recuperer le resultat du redirect Google si applicable
      // (apres signInWithRedirect, Google redirige vers la page et
      // onAuthStateChanged se declenche, mais getRedirectResult finalise la session)
      if (firebaseModule.isSafariBrowser()) {
        try {
          await firebaseModule.getRedirectResult(firebaseModule.auth);
        } catch (err) {
          // Ignorer les erreurs (ex: pas de redirect en cours, ou popup_closed)
          if (err?.code !== 'auth/no-auth-event') {
            console.warn('getRedirectResult error:', err?.code);
          }
        }
      }

      unsubscribe = authModule.onAuthStateChanged(firebaseModule.auth, async (firebaseUser) => {
        if (firebaseUser) {
          setUser(firebaseUser);

          // Lazy-load Firestore functions on first login
          if (!dbRef.current) {
            dbRef.current = await import('./firebase-db');
          }
          const db = dbRef.current;

          // Souscription aux contacts personnels (Firestore)
          contactsUnsubRef.current = db.subscribeToContacts(firebaseUser.uid, (firestoreContacts) => {
            setContacts(firestoreContacts);
          });

          // Souscription aux groupes privés
          groupsUnsubRef.current = db.subscribeToGroups(firebaseUser.uid, (groups) => {
            setLocalGroups(groups);
          });

          // Souscription aux dossiers partagés où l'email est membre
          foldersUnsubRef.current = db.subscribeToSharedFolders(firebaseUser.email, (folders) => {
            setSharedFolders(folders);
          });

          // Souscription au profil utilisateur
          profileUnsubRef.current = db.subscribeToUserProfile(firebaseUser.uid, (profile, metadata) => {
            if (metadata.hasPendingWrites) {
              // Snapshot optimiste local : merger pour ne pas écraser phone/instagram
              setUserProfile(prev => ({ ...prev, ...profile }));
            } else {
              // Snapshot confirmé par le serveur : données complètes fiables
              setUserProfile(profile);
            }
            // Charger les contacts partagés cachés
            if (profile.hiddenSharedContactIds) {
              setHiddenSharedContactIds(new Set(profile.hiddenSharedContactIds));
            }
          });

          // Persister le displayName/email dans Firestore pour le partage de profil
          const nameParts = (firebaseUser.displayName || '').trim().split(' ');
          db.saveUserProfile(firebaseUser.uid, {
            displayName: firebaseUser.displayName || '',
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
            email: firebaseUser.email || '',
          }).catch(() => {}); // fire-and-forget, non bloquant
        } else {
          setUser(null);
          setContacts([]);
          setLocalGroups([]);
          setSharedFolders([]);
          setFolderContactsMap({});
          setSelectedFolder(null);
          setSelectedGroup(null);
          setSharingGroup(null);
          if (contactsUnsubRef.current) contactsUnsubRef.current();
          if (groupsUnsubRef.current) groupsUnsubRef.current();
          if (foldersUnsubRef.current) foldersUnsubRef.current();
          if (profileUnsubRef.current) profileUnsubRef.current();
          Object.values(folderContactUnsubsRef.current).forEach(fn => fn());
          folderContactUnsubsRef.current = {};
        }
        setAuthLoading(false);
      });
    };

    initAuth();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
      if (contactsUnsubRef.current) contactsUnsubRef.current();
      if (groupsUnsubRef.current) groupsUnsubRef.current();
      if (foldersUnsubRef.current) foldersUnsubRef.current();
      if (profileUnsubRef.current) profileUnsubRef.current();
      Object.values(folderContactUnsubsRef.current).forEach(fn => fn());
    };
  }, []);

  // ── Détection du lien de partage de profil (?share=uid) ──────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareUid = params.get('share');
    if (!shareUid) return;
    // Nettoyer l'URL sans recharger la page
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
    // Charger le profil public
    const loadSharedProfile = async () => {
      setSharedProfileLoading(true);
      try {
        if (!dbRef.current) {
          dbRef.current = await import('./firebase-db');
        }
        const profile = await dbRef.current.getPublicProfile(shareUid);
        if (profile) {
          setSharedProfileData({ uid: shareUid, ...profile });
        }
      } catch (err) {
        console.error('Erreur chargement profil partagé:', err);
      } finally {
        setSharedProfileLoading(false);
      }
    };
    loadSharedProfile();
  }, []);

  const handleAddSharedContact = async () => {
    if (!sharedProfileData) return;
    const contact = {
      firstName: sharedProfileData.firstName || '',
      lastName: sharedProfileData.lastName || '',
      phone: sharedProfileData.phone || '',
      email: sharedProfileData.email || '',
      instagram: sharedProfileData.instagram || '',
      labels: [],
    };
    if (user) {
      await handleSaveContact(contact);
    }
    setSharedProfileData(null);
  };

  // Souscription dynamique aux contacts de chaque dossier partagé
  useEffect(() => {
    if (!user || !dbRef.current) return;
    const currentIds = sharedFolders.map(f => f.id);
    const prevIds = Object.keys(folderContactUnsubsRef.current);

    // Désabonner les dossiers supprimés
    prevIds.forEach(fid => {
      if (!currentIds.includes(fid)) {
        folderContactUnsubsRef.current[fid]();
        delete folderContactUnsubsRef.current[fid];
        setFolderContactsMap(prev => {
          const next = { ...prev };
          delete next[fid];
          return next;
        });
      }
    });

    // Abonner les nouveaux dossiers
    currentIds.forEach(fid => {
      if (!folderContactUnsubsRef.current[fid]) {
        folderContactUnsubsRef.current[fid] = dbRef.current.subscribeToFolderContacts(fid, (fContacts) => {
          setFolderContactsMap(prev => ({ ...prev, [fid]: fContacts }));
        });
      }
    });
  }, [sharedFolders, user]);

  // ── Android Back Button Handler ────────────────────────────────────────────
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let backHandler;
    const setupBackButton = async () => {
      try {
        const { App: CapApp } = await import('@capacitor/app');
        backHandler = CapApp.addListener('backButton', ({ canGoBack }) => {
          // Fermer les modals en priorité
          if (isScannerOpen) { setIsScannerOpen(false); return; }
          if (scannedContact) { setScannedContact(null); return; }
          if (viewingContact) { setViewingContact(null); return; }
          if (isContactModalOpen) { setIsContactModalOpen(false); setEditingContact(null); return; }
          if (isGroupModalOpen) { setIsGroupModalOpen(false); return; }
          if (sharingGroup) { setSharingGroup(null); return; }
          if (isFolderModalOpen) { setIsFolderModalOpen(false); return; }

          // Revenir de la vue détail
          if (selectedGroup) { setSelectedGroup(null); return; }
          if (selectedFolder) { setSelectedFolder(null); return; }

          // Revenir à l'onglet principal
          if (activeTab !== 'all') { setActiveTab('all'); return; }

          // Sinon, minimiser l'app
          CapApp.minimizeApp();
        });
      } catch {
        // @capacitor/app not available (web mode)
      }
    };

    setupBackButton();
    return () => { if (backHandler) backHandler.remove(); };
  }, [isScannerOpen, scannedContact, viewingContact, isContactModalOpen, isGroupModalOpen, sharingGroup, isFolderModalOpen, selectedGroup, selectedFolder, activeTab]);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      // Wait for Firebase to be loaded if not yet ready
      if (!authFnsRef.current.signInWithGoogle) {
        const firebaseModule = await import('./firebase');
        authFnsRef.current.signInWithGoogle = firebaseModule.signInWithGoogle;
        authFnsRef.current.signOutUser = firebaseModule.signOutUser;
      }
      await authFnsRef.current.signInWithGoogle();
    } catch (err) {
      console.error('Erreur connexion Google:', err);
      alert('Erreur lors de la connexion. Vérifiez la configuration Firebase.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    // Nettoyer toutes les souscriptions Firestore
    if (contactsUnsubRef.current) contactsUnsubRef.current();
    if (groupsUnsubRef.current) groupsUnsubRef.current();
    if (foldersUnsubRef.current) foldersUnsubRef.current();
    Object.values(folderContactUnsubsRef.current).forEach(fn => fn());
    folderContactUnsubsRef.current = {};
    if (authFnsRef.current.signOutUser) {
      await authFnsRef.current.signOutUser();
    }
    setUser(null);
    setContacts([]);
    setLocalGroups([]);
    setSharedFolders([]);
    setFolderContactsMap({});
    setSelectedFolder(null);
    setSelectedGroup(null);
    setSharingGroup(null);
    setActiveTab('all');
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    try {
      // Supprimer toutes les données Firestore de l'utilisateur
      await dbRef.current.deleteAllUserData(user.uid);
    } catch (err) {
      console.error('Erreur suppression données:', err);
    }
    // Déconnecter et réinitialiser
    await handleSignOut();
    setShowDeleteConfirm(false);
  };

  // Sauvegarder un contact (création ou édition)
  const handleSaveContact = async (contactData) => {
    if (!user) return;
    const contactToSave = {
      id: contactData.id || `c-${Date.now()}`,
      firstName: contactData.firstName,
      lastName: contactData.lastName,
      phone: contactData.phone || '',
      email: contactData.email || '',
      instagram: contactData.instagram || '',
      labels: contactData.labels || [],
    };
    console.log('📋 Saving contact:', JSON.stringify(contactToSave));
    try {
      await dbRef.current.saveContact(user.uid, contactToSave);
      setIsContactModalOpen(false);
      setEditingContact(null);
    } catch (err) {
      console.error('Erreur sauvegarde contact:', err);
      alert(`Erreur lors de la sauvegarde : ${err.message}`);
    }
  };

  // Supprimer un contact
  const handleDeleteContact = async (contactId, isShared = false) => {
    if (!user) return;
    if (isShared) {
      // Contact partagé : masquer uniquement pour soi
      const newHidden = new Set(hiddenSharedContactIds);
      newHidden.add(contactId);
      setHiddenSharedContactIds(newHidden);
      // Persister dans le profil Firestore
      await dbRef.current.saveUserProfile(user.uid, {
        hiddenSharedContactIds: Array.from(newHidden),
      });
    } else {
      // Contact privé : supprimer de Firestore
      await dbRef.current.deleteContact(user.uid, contactId);
    }
  };


  // Contacts partagés depuis Firestore (tous dossiers confondus, dédupliqués, sans les cachés)
  const sharedContacts = useMemo(() => {
    const all = Object.values(folderContactsMap).flat();
    const seen = new Set();
    return all.filter(c => {
      if (seen.has(c.email)) return false;
      if (hiddenSharedContactIds.has(c.id)) return false;
      seen.add(c.email);
      return true;
    });
  }, [folderContactsMap, hiddenSharedContactIds]);

  // Répertoire complet : contacts personnels + contacts partagés (sans doublons)
  const allContacts = useMemo(() => {
    const localEmails = new Set(contacts.filter(c => c.email).map(c => c.email));
    const uniqueShared = sharedContacts.filter(c => c.email && !localEmails.has(c.email));
    return [...contacts, ...uniqueShared];
  }, [contacts, sharedContacts]);

  // Labels existants extraits de tous les contacts
  const existingLabels = useMemo(() => {
    const set = new Set();
    allContacts.forEach(c => c.labels?.forEach(l => set.add(l)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [allContacts]);

  const filteredContacts = useMemo(() => {
    const list = allContacts.filter((c) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        c.name.toLowerCase().includes(q) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.phone && c.phone.toLowerCase().includes(q)) ||
        (c.instagram && c.instagram.toLowerCase().includes(q)) ||
        (c.labels && c.labels.some(l => l.toLowerCase().includes(q)));
      const matchesSource =
        contactSourceFilter === 'all' ||
        (contactSourceFilter === 'mine' && !c.isShared) ||
        (contactSourceFilter === 'shared' && c.isShared);

      const matchesGroup =
        selectedGroupFilter === 'all' ||
        localGroups.find((g) => g.id === selectedGroupFilter)?.memberIds.includes(c.id);

      const matchesLabel =
        selectedLabelFilter === 'all' ||
        (c.labels && c.labels.includes(selectedLabelFilter));

      return matchesSearch && matchesSource && matchesGroup && matchesLabel;
    });

    return [...list].sort((a, b) => {
      if (a.isStarred && !b.isStarred) return -1;
      if (!a.isStarred && b.isStarred) return 1;
      return a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });
    });
  }, [allContacts, searchQuery, contactSourceFilter, selectedGroupFilter, localGroups, selectedLabelFilter]);

  const getContactFolders = (contactId) => {
    const userGroupIds = localGroups
      .filter((g) => g.memberIds.includes(contactId))
      .map((g) => g.id);
    return sharedFolders.filter((f) =>
      f.groupIds.some((gId) => userGroupIds.includes(gId))
    );
  };


  const createManualGroup = async (groupData) => {
    const newGroup = {
      id: generateGroupId(),
      name: groupData.name,
      memberIds: groupData.memberIds || [],
    };
    if (user) {
      await dbRef.current.saveGroup(user.uid, newGroup);
    } else {
      setLocalGroups((prev) => [...prev, newGroup]);
    }
    setIsGroupModalOpen(false);
  };

  const addContactToGroup = async (groupId, contactId) => {
    const group = localGroups.find((g) => g.id === groupId);
    if (!group) return;
    if (group.memberIds.includes(contactId)) return;

    const updatedGroup = {
      ...group,
      memberIds: [...group.memberIds, contactId],
    };
    if (user) {
      await dbRef.current.saveGroup(user.uid, updatedGroup);
      const linkedFolders = sharedFolders.filter((f) => f.groupIds?.includes(groupId) && f.createdBy === user.email);
      for (const f of linkedFolders) {
        const contact = allContacts.find((c) => c.id === contactId);
        if (contact) {
          await dbRef.current.saveContactToFolder(f.id, contact, user.email);
        }
      }
    } else {
      setLocalGroups((prev) => prev.map((g) => (g.id === groupId ? updatedGroup : g)));
    }
  };

  const removeContactFromGroup = async (groupId, contactId) => {
    const group = localGroups.find((g) => g.id === groupId);
    if (!group) return;

    const updatedGroup = {
      ...group,
      memberIds: group.memberIds.filter((id) => id !== contactId),
    };
    if (user) {
      await dbRef.current.saveGroup(user.uid, updatedGroup);
      const linkedFolders = sharedFolders.filter((f) => f.groupIds?.includes(groupId) && f.createdBy === user.email);
      for (const f of linkedFolders) {
        await dbRef.current.removeContactFromFolder(f.id, contactId);
      }
    } else {
      setLocalGroups((prev) => prev.map((g) => (g.id === groupId ? updatedGroup : g)));
    }
  };

  const renameGroup = async (groupId, newName) => {
    const group = localGroups.find((g) => g.id === groupId);
    if (!group) return;

    const updatedGroup = {
      ...group,
      name: newName,
    };
    if (user) {
      await dbRef.current.saveGroup(user.uid, updatedGroup);
      const linkedFolders = sharedFolders.filter((f) => f.groupIds?.includes(groupId) && f.createdBy === user.email);
      for (const f of linkedFolders) {
        const updatedFolder = {
          ...f,
          name: newName,
        };
        await dbRef.current.saveSharedFolder(updatedFolder);
      }
    } else {
      setLocalGroups((prev) => prev.map((g) => (g.id === groupId ? updatedGroup : g)));
    }
  };

  const handleShareGroup = async (group, targetGmail) => {
    if (!user) return;
    // Toujours utiliser la version la plus récente du groupe depuis localGroups
    const liveGroup = localGroups.find((g) => g.id === group.id) || group;
    const existingShared = sharedFolders.find(
      (f) => f.groupIds?.includes(liveGroup.id) && f.createdBy === user.email
    );

    try {
      if (existingShared) {
        if (!existingShared.members.includes(targetGmail.toLowerCase())) {
          const updatedFolder = {
            ...existingShared,
            members: Array.from(new Set([...existingShared.members, targetGmail.toLowerCase()])),
          };
          await dbRef.current.saveSharedFolder(updatedFolder);
        }
      } else {
        const folderId = generateFolderId();
        const newFolder = {
          id: folderId,
          name: liveGroup.name,
          members: Array.from(new Set([user.email.toLowerCase(), targetGmail.toLowerCase()])),
          groupIds: [liveGroup.id],
          color: 'blue',
          createdBy: user.email,
        };
        await dbRef.current.saveSharedFolder(newFolder);
        // Copier tous les contacts existants du groupe vers le dossier partagé
        const groupContacts = allContacts.filter((c) => (liveGroup.memberIds || []).includes(c.id));
        for (const contact of groupContacts) {
          await dbRef.current.saveContactToFolder(folderId, contact, user.email);
        }
      }
    } catch (e) {
      console.error("Erreur lors du partage du groupe :", e);
    }
  };

  const deleteGroupHandler = async (groupId) => {
    if (user) {
      // Pour les dossiers partagés liés à ce groupe :
      // - si on est le créateur ET seul/dernier membre → supprimer le dossier
      // - sinon → se retirer des membres uniquement (ne pas supprimer pour les autres)
      const linkedFolders = sharedFolders.filter(
        (f) => f.groupIds?.includes(groupId)
      );
      for (const f of linkedFolders) {
        const remainingMembers = f.members.filter(
          (m) => m.toLowerCase() !== user.email.toLowerCase()
        );
        if (remainingMembers.length === 0) {
          // Dernier membre → supprimer définitivement
          await dbRef.current.deleteSharedFolder(f.id);
        } else {
          // D'autres membres restent → se retirer seulement
          await dbRef.current.saveSharedFolder({ ...f, members: remainingMembers });
        }
      }
      // Supprimer le groupe privé (uniquement pour soi)
      await dbRef.current.deleteGroup(user.uid, groupId);
    } else {
      setLocalGroups((prev) => prev.filter((g) => g.id !== groupId));
    }
  };

  const removeCollaborator = async (groupId, emailToRemove) => {
    if (!user) return;
    const linkedFolder = sharedFolders.find(
      (f) => f.groupIds?.includes(groupId) && f.createdBy === user.email
    );
    if (!linkedFolder) return;
    const updatedMembers = linkedFolder.members.filter(
      (m) => m.toLowerCase() !== emailToRemove.toLowerCase()
    );
    // S'il ne reste que le créateur, supprimer le dossier partagé
    if (updatedMembers.length <= 1) {
      await dbRef.current.deleteSharedFolder(linkedFolder.id);
    } else {
      await dbRef.current.saveSharedFolder({ ...linkedFolder, members: updatedMembers });
    }
  };

  const addSharedFolder = async (folderData) => {
    const newFolder = {
      ...folderData,
      id: generateFolderId(),
      createdBy: user.email,
      // Le créateur est automatiquement membre
      members: Array.from(new Set([...(folderData.members || []), user.email])),
    };
    await dbRef.current.saveSharedFolder(newFolder); // Persisté dans Firestore → onSnapshot met à jour le state
    setIsFolderModalOpen(false);
  };

  // Ajoute un contact local dans un dossier partagé Firestore
  const addContactToSharedFolder = async (folderId, contact) => {
    if (!user) return;
    await dbRef.current.saveContactToFolder(folderId, contact, user.email);
  };

  // Retire un contact d'un dossier partagé (conservé pour compatibilité API locale)
  // const removeContactFromSharedFolder = async (folderId, contactId) => {
  //   await dbRef.current.removeContactFromFolder(folderId, contactId);
  // };

  // Auth loading is handled inline — no blocking spinner, login page renders immediately

  // ── Login screen ──────────────────────────────────────────────────────────
  if (!user) {
    return (
      <main
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          position: 'relative',
          overflow: 'hidden',
          background: '#0f1623',
          fontFamily: 'Inter, sans-serif',
          color: '#f1f5f9',
        }}
      >
        {/* BG glow */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 50% 0%, rgba(30,58,138,0.25), #0f1623 70%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            width: '100%',
            maxWidth: 440,
            background: 'rgba(30,40,64,0.85)',
            backdropFilter: 'blur(24px)',
            borderRadius: isMobile ? 20 : 28,
            padding: isMobile ? '32px 20px' : '48px 40px',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
            textAlign: 'center',
            position: 'relative',
            zIndex: 10,
          }}
        >
          {/* Icon */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                padding: 16,
                background: 'rgba(59,130,246,0.15)',
                borderRadius: 20,
                border: '1px solid rgba(59,130,246,0.25)',
              }}
            >
              <Users size={40} color="#60a5fa" />
            </div>
          </div>

          <h1
            style={{
              fontSize: isMobile ? 28 : 38,
              fontWeight: 800,
              letterSpacing: '-0.04em',
              marginBottom: 10,
              background: 'linear-gradient(to bottom right, #fff, #94a3b8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            TuConnaisQui
          </h1>
          <p
            style={{
              color: '#64748b',
              marginBottom: 40,
              fontSize: 16,
              lineHeight: 1.6,
            }}
          >
            Organisez vos relations avec l'élégance de demain.
          </p>

          {/* Google Sign-In button */}
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            style={{
              width: '100%',
              background: '#fff',
              color: '#111',
              fontWeight: 700,
              fontSize: 15,
              padding: '16px 24px',
              borderRadius: 16,
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              transition: 'all 0.2s',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            }}
            onMouseEnter={(e) => !isLoading && (e.currentTarget.style.background = '#eff6ff')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
          >
            {isLoading ? (
              <div
                style={{
                  width: 20,
                  height: 20,
                  border: '2px solid rgba(0,0,0,0.15)',
                  borderTopColor: '#000',
                  borderRadius: '50%',
                }}
                className="animate-spin"
              />
            ) : (
              <>
                {/* Google SVG icon */}
                <svg width="20" height="20" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                </svg>
                Se connecter avec Google
              </>
            )}
          </button>

          <p
            style={{
              marginTop: 20,
              fontSize: 12,
              color: '#64748b',
              lineHeight: 1.6,
            }}
          >
            Connectez-vous pour gérer vos contacts et les partager avec vos proches.
          </p>
        </div>
      </main>
    );
  }

  // ── Main App ──────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        background: '#0f1623',
        color: '#f1f5f9',
        overflow: 'hidden',
        position: 'relative',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* BG */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 20% 0%, #0d2060, #0f1623 75%)',
          pointerEvents: 'none',
        }}
      />

      {/* ── Sidebar (desktop only) ── */}
      {!isMobile && (
        <aside
          className={isMobile ? 'sidebar-mobile-enter' : undefined}
          style={{
            width: 280,
            background: isMobile ? '#171f2e' : 'rgba(23,31,46,0.7)',
            backdropFilter: isMobile ? 'none' : 'blur(20px)',
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid rgba(255,255,255,0.05)',
            position: isMobile ? 'fixed' : 'relative',
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: isMobile ? 50 : 20,
            flexShrink: 0,
            boxShadow: isMobile ? '8px 0 40px rgba(0,0,0,0.5)' : 'none',
          }}
        >
          {/* Logo */}
          <div style={{ padding: '32px 28px', display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  padding: 8,
                  background: 'rgba(59,130,246,0.15)',
                  borderRadius: 12,
                  border: '1px solid rgba(59,130,246,0.2)',
                }}
              >
                <Users size={22} color="#60a5fa" />
              </div>
              <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.03em', color: '#fff' }}>
                TuConnaisQui
              </span>
            </div>
            {isMobile && (
              <button
                onClick={closeSidebar}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10,
                  padding: 6,
                  color: '#94a3b8',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChevronLeft size={18} />
              </button>
            )}
          </div>

          {/* Nav */}
          <nav style={{ padding: '0 12px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SidebarItem icon={<LayoutDashboard size={18} />} label="Répertoire" active={activeTab === 'all'} onClick={() => handleTabChange('all')} />
            <SidebarItem icon={<Users size={18} />} label="Mes Groupes" active={activeTab === 'groups'} onClick={() => handleTabChange('groups')} badge={localGroups.length} />

            {/* Sub-items: shared folders under Mes Groupes */}
            {sharedFolders.filter(f => f.createdBy === user.email).map((folder) => (
              <button
                key={folder.id}
                onClick={() => { setSelectedFolder(folder); handleTabChange('sharing'); }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 14px 6px 26px',
                  fontSize: 12,
                  color: selectedFolder?.id === folder.id && activeTab === 'sharing' ? '#60a5fa' : '#7a8aa0',
                  background: selectedFolder?.id === folder.id && activeTab === 'sharing' ? 'rgba(59,130,246,0.06)' : 'none',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'color 0.15s',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#60a5fa')}
                onMouseLeave={(e) => { if (!(selectedFolder?.id === folder.id && activeTab === 'sharing')) e.currentTarget.style.color = '#7a8aa0'; }}
              >
                <Share2 size={12} style={{ opacity: 0.5, flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.name}</span>
              </button>
            ))}

            <div style={{ padding: '20px 12px 6px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#7a8aa0', fontWeight: 700 }}>
              Partage
            </div>

            <SidebarItem icon={<Share2 size={18} />} label="Groupes Partagés" active={activeTab === 'sharing'} onClick={() => { handleTabChange('sharing'); setSelectedFolder(null); }} badge={sharedFolders.filter(f => f.createdBy !== user.email).length} />


          </nav>

          {/* User + Sign Out */}
          <div
            style={{
              padding: '20px 20px',
              borderTop: '1px solid rgba(255,255,255,0.05)',
              background: 'rgba(23,31,46,0.4)',
            }}
          >
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer', padding: '8px 10px', borderRadius: 12, transition: 'background 0.15s', marginLeft: -10, marginRight: -10 }}
              onClick={() => handleTabChange('settings')}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              {user.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={user.displayName}
                  style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid rgba(59,130,246,0.3)' }}
                />
              ) : (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: 'rgba(59,130,246,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#60a5fa',
                  }}
                >
                  <User size={18} />
                </div>
              )}
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.displayName || 'Utilisateur'}
                </div>
                <div style={{ fontSize: 11, color: '#7a8aa0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.email}
                </div>
              </div>
              <Settings size={14} color="#64748b" style={{ flexShrink: 0 }} />
            </div>
            <button
              onClick={handleSignOut}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: '#94a3b8',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#f87171')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
            >
              <LogOut size={16} />
              Déconnexion
            </button>
          </div>
        </aside>
      )}

      {/* ── Main ── */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', zIndex: 10 }}>
        {/* Header */}
        <header
          style={{
            padding: isMobile ? '14px 16px' : '20px 40px',
            paddingTop: isMobile ? 'calc(14px + env(safe-area-inset-top, 0px))' : '20px',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 10 : 16,
            borderBottom: '1px solid rgba(255,255,255,0.04)',
          }}
        >

          <div style={{ position: 'relative', flex: 1, maxWidth: isMobile ? '100%' : 600 }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: 18,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#7a8aa0',
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              placeholder="Rechercher un contact, un tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowSearchSuggestions(true)}
              style={{
                width: '100%',
                background: 'rgba(30,40,64,0.6)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 16,
                padding: '12px 20px 12px 44px',
                fontSize: 14,
                color: '#f1f5f9',
                outline: 'none',
                backdropFilter: 'blur(12px)',
                transition: 'border-color 0.2s',
                fontFamily: 'inherit',
              }}
              onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 200)}
            />
            {showSearchSuggestions && searchQuery.trim().length > 0 && (() => {
              const q = searchQuery.toLowerCase();
              const matchedContacts = allContacts.filter(c =>
                c.name.toLowerCase().includes(q) ||
                (c.email && c.email.toLowerCase().includes(q)) ||
                (c.phone && c.phone.toLowerCase().includes(q)) ||
                (c.instagram && c.instagram.toLowerCase().includes(q))
              ).slice(0, 5);
              const matchedTags = existingLabels.filter(l => l.toLowerCase().includes(q)).slice(0, 5);
              if (matchedContacts.length === 0 && matchedTags.length === 0) return null;
              return (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                  background: 'rgba(25,35,55,0.98)', backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(99,102,241,0.2)', borderRadius: 16,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)', zIndex: 60,
                  overflow: 'hidden', maxHeight: 360, overflowY: 'auto',
                }}>
                  {matchedContacts.length > 0 && (
                    <>
                      <div style={{ padding: '10px 16px 6px', fontSize: 10, fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Contacts</div>
                      {matchedContacts.map(c => (
                        <button
                          key={c.id}
                          onMouseDown={(e) => { e.preventDefault(); setViewingContact(c); setSearchQuery(''); setShowSearchSuggestions(false); }}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                            padding: '10px 16px', background: 'none', border: 'none',
                            cursor: 'pointer', color: '#f1f5f9', textAlign: 'left',
                            transition: 'background 0.15s', fontFamily: 'inherit',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                          {c.photo
                            ? <img src={c.photo} alt={c.name} style={{ width: 32, height: 32, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                            : <div style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{c.name[0]}</div>
                          }
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                            <div style={{ fontSize: 11, color: '#7a8aa0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email || c.phone || c.instagram || ''}</div>
                          </div>
                          {c.labels && c.labels.length > 0 && (
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                              {c.labels.slice(0, 2).map(l => (
                                <span key={l} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 6, background: 'rgba(168,85,247,0.1)', color: '#c084fc', fontWeight: 600 }}>{l}</span>
                              ))}
                            </div>
                          )}
                        </button>
                      ))}
                    </>
                  )}
                  {matchedTags.length > 0 && (
                    <>
                      {matchedContacts.length > 0 && <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 12px' }} />}
                      <div style={{ padding: '10px 16px 6px', fontSize: 10, fontWeight: 700, color: '#c084fc', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Tags</div>
                      {matchedTags.map(tag => (
                        <button
                          key={tag}
                          onMouseDown={(e) => { e.preventDefault(); setSelectedLabelFilter(tag); setActiveTab('all'); setSearchQuery(''); setShowSearchSuggestions(false); }}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 16px', background: 'none', border: 'none',
                            cursor: 'pointer', color: '#f1f5f9', textAlign: 'left',
                            transition: 'background 0.15s', fontFamily: 'inherit',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(168,85,247,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                          <div style={{ padding: 6, borderRadius: 8, background: 'rgba(168,85,247,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Tag size={14} color="#c084fc" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{tag}</div>
                            <div style={{ fontSize: 11, color: '#7a8aa0' }}>{allContacts.filter(c => c.labels?.includes(tag)).length} contact(s)</div>
                          </div>
                          <ArrowRight size={12} color="#64748b" />
                        </button>
                      ))}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
          {isMobile && (
            <button
              onClick={() => setShowCameraPermissionModal(true)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 44, height: 44, flexShrink: 0,
                background: 'rgba(99,102,241,0.1)',
                border: '1px solid rgba(99,102,241,0.2)',
                borderRadius: 14, color: '#818cf8',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#4f46e5'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.1)'; e.currentTarget.style.color = '#818cf8'; }}
            >
              <QrCode size={20} />
            </button>
          )}
        </header>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '20px 16px' : '32px 40px', paddingBottom: isMobile ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : undefined }}>
          {/* ── Directory Tab ── */}
          {activeTab === 'all' && (
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
              {/* Mobile compact header */}
              {isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 2 }}>Répertoire</h2>
                      <p style={{ color: '#7a8aa0', fontSize: 12 }}>
                        {allContacts.length > 0
                          ? `${contacts.length} contact${contacts.length !== 1 ? 's' : ''} · ${sharedContacts.length} partagé${sharedContacts.length !== 1 ? 's' : ''}`
                          : 'Ajoutez vos premiers contacts'}
                      </p>
                    </div>
                    <button
                      onClick={() => { setEditingContact(null); setIsContactModalOpen(true); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: '#2563eb', color: '#fff', border: 'none',
                        padding: '9px 14px', borderRadius: 12, fontSize: 12,
                        fontWeight: 700, cursor: 'pointer',
                        boxShadow: '0 4px 16px rgba(37,99,235,0.25)',
                        fontFamily: 'inherit',
                      }}
                    >
                      <Plus size={14} /> Ajouter
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', background: 'rgba(30,40,64,0.8)', padding: 3, borderRadius: 10, border: '1px solid rgba(255,255,255,0.05)', flex: 1 }}>
                      <FilterButton label="Tout" active={contactSourceFilter === 'all'} onClick={() => setContactSourceFilter('all')} />
                      <FilterButton label="Perso" active={contactSourceFilter === 'mine'} onClick={() => setContactSourceFilter('mine')} />
                      <FilterButton label="Partagés" active={contactSourceFilter === 'shared'} onClick={() => setContactSourceFilter('shared')} />
                    </div>
                    {localGroups.length > 0 && (
                      <select
                        value={selectedGroupFilter}
                        onChange={(e) => setSelectedGroupFilter(e.target.value)}
                        style={{
                          background: 'rgba(30,40,64,0.8)', border: '1px solid rgba(255,255,255,0.05)',
                          borderRadius: 10, padding: '7px 10px', color: '#f1f5f9',
                          fontSize: 11, fontWeight: 600, outline: 'none', cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        <option value="all" style={{ background: '#1e2840' }}>Groupes</option>
                        {localGroups.map(g => <option key={g.id} value={g.id} style={{ background: '#1e2840' }}>{g.name}</option>)}
                      </select>
                    )}
                    {existingLabels.length > 0 && (
                      <LabelFilterDropdown
                        existingLabels={existingLabels}
                        selectedLabelFilter={selectedLabelFilter}
                        setSelectedLabelFilter={setSelectedLabelFilter}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    <div>
                      <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>Répertoire</h2>
                      <p style={{ color: '#7a8aa0', fontSize: 14 }}>
                        {allContacts.length > 0
                          ? `${contacts.length} contact${contacts.length !== 1 ? 's' : ''} · ${sharedContacts.length} partagé${sharedContacts.length !== 1 ? 's' : ''}`
                          : 'Ajoutez vos premiers contacts pour commencer'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div
                        style={{
                          display: 'flex',
                          background: 'rgba(30,40,64,0.8)',
                          padding: 3,
                          borderRadius: 14,
                          border: '1px solid rgba(255,255,255,0.05)',
                        }}
                      >
                        <FilterButton label="Tout" active={contactSourceFilter === 'all'} onClick={() => setContactSourceFilter('all')} />
                        <FilterButton label="Perso" active={contactSourceFilter === 'mine'} onClick={() => setContactSourceFilter('mine')} />
                        <FilterButton label="Partagés" active={contactSourceFilter === 'shared'} onClick={() => setContactSourceFilter('shared')} />
                      </div>
                      <button
                        onClick={() => { setEditingContact(null); setIsContactModalOpen(true); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          background: '#2563eb', color: '#fff', border: 'none',
                          padding: '12px 20px', borderRadius: 14, fontSize: 13,
                          fontWeight: 700, cursor: 'pointer',
                          boxShadow: '0 4px 20px rgba(37,99,235,0.25)', transition: 'all 0.2s',
                          fontFamily: 'inherit',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#3b82f6')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = '#2563eb')}
                      >
                        <Plus size={16} /> Nouveau Contact
                      </button>
                    </div>
                  </div>

                  {/* Filtre par Groupe */}
                  {allContacts.length > 0 && localGroups.length > 0 && (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(30,40,64,0.4)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: '6px 14px' }}>
                        <span style={{ fontSize: 12, color: '#7a8aa0', fontWeight: 600 }}>Groupe :</span>
                        <select
                          value={selectedGroupFilter}
                          onChange={(e) => setSelectedGroupFilter(e.target.value)}
                          style={{ background: 'none', border: 'none', color: '#f1f5f9', fontSize: 12, fontWeight: 700, outline: 'none', cursor: 'pointer' }}
                        >
                          <option value="all" style={{ background: '#1e2840' }}>Tous les groupes</option>
                          {localGroups.map((g) => (
                            <option key={g.id} value={g.id} style={{ background: '#1e2840' }}>{g.name}</option>
                          ))}
                        </select>
                      </div>
                      {selectedGroupFilter !== 'all' && (
                        <button
                          onClick={() => setSelectedGroupFilter('all')}
                          style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, transition: 'background 0.15s' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >Réinitialiser</button>
                      )}
                    </div>
                  )}

                  {/* Label filter */}
                  {existingLabels.length > 0 && (
                    <LabelFilterDropdown
                      existingLabels={existingLabels}
                      selectedLabelFilter={selectedLabelFilter}
                      setSelectedLabelFilter={setSelectedLabelFilter}
                    />
                  )}
                </div>
              )}

              {/* Contact list */}
              {isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {filteredContacts.map((contact) => (
                    <MobileContactRow
                      key={contact.id}
                      contact={contact}
                      expanded={expandedContactId === contact.id}
                      onToggle={() => setExpandedContactId(expandedContactId === contact.id ? null : contact.id)}
                      onViewDetail={() => setViewingContact(contact)}
                      onEdit={() => { setEditingContact(contact); setIsContactModalOpen(true); }}
                      onDelete={() => handleDeleteContact(contact.id, contact.isShared)}
                      localGroups={localGroups}
                      onAddToGroup={(groupId) => addContactToGroup(groupId, contact.id)}
                      onNavigateToGroup={(group) => { setActiveTab('groups'); setSelectedGroup(group); }}
                      sharedFolders={getContactFolders(contact.id)}
                      availableFolders={sharedFolders}
                      onAddToFolder={(folderId) => addContactToSharedFolder(folderId, contact)}
                      onNavigateToFolder={(folder) => { setActiveTab('shared'); setSelectedFolder(folder); }}
                    />
                  ))}
                  {filteredContacts.length === 0 && (
                    <div style={{ padding: '60px 0', textAlign: 'center', border: '2px dashed rgba(255,255,255,0.05)', borderRadius: 24 }}>
                      <UserPlus size={32} style={{ margin: '0 auto 10px', color: '#2d3a52' }} />
                      <p style={{ color: '#64748b', fontSize: 13 }}>
                        {allContacts.length === 0 ? 'Aucun contact.' : 'Aucun résultat.'}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 20,
                  }}
                >
                  {filteredContacts.map((contact) => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      sharedFolders={getContactFolders(contact.id)}
                      availableFolders={sharedFolders}
                      onAddToFolder={(folderId) => addContactToSharedFolder(folderId, contact)}
                      localGroups={localGroups}
                      onAddToGroup={(groupId) => addContactToGroup(groupId, contact.id)}
                      onNavigateToGroup={(group) => { setActiveTab('groups'); setSelectedGroup(group); }}
                      onNavigateToFolder={(folder) => { setActiveTab('shared'); setSelectedFolder(folder); }}
                      onEdit={() => { setEditingContact(contact); setIsContactModalOpen(true); }}
                      onDelete={() => handleDeleteContact(contact.id, contact.isShared)}
                      onClick={() => setViewingContact(contact)}
                    />
                  ))}
                  {filteredContacts.length === 0 && (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        padding: '80px 0',
                        textAlign: 'center',
                        border: '2px dashed rgba(255,255,255,0.05)',
                        borderRadius: 32,
                      }}
                    >
                      <UserPlus size={40} style={{ margin: '0 auto 12px', color: '#2d3a52' }} />
                      <p style={{ color: '#64748b', marginBottom: 8 }}>
                        {allContacts.length === 0 ? 'Aucun contact pour le moment.' : 'Aucun contact ne correspond à votre recherche.'}
                      </p>
                      {allContacts.length === 0 && (
                        <button
                          onClick={() => { setEditingContact(null); setIsContactModalOpen(true); }}
                          style={{
                            marginTop: 12,
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            background: '#2563eb', border: 'none', borderRadius: 14, padding: '12px 24px',
                            color: '#fff', fontSize: 13, fontWeight: 700,
                            cursor: 'pointer', boxShadow: '0 4px 20px rgba(37,99,235,0.3)',
                            transition: 'all 0.2s', fontFamily: 'inherit',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#3b82f6')}
                          onMouseLeave={e => (e.currentTarget.style.background = '#2563eb')}
                        >
                          <Plus size={16} /> Ajouter un contact
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Sharing Tab ── */}
          {activeTab === 'sharing' && (
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
              {selectedFolder ? (
                <FolderDetailView
                  folder={selectedFolder}
                  folderContacts={folderContactsMap[selectedFolder.id] || []}
                  onBack={() => setSelectedFolder(null)}
                />
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 32 }}>
                    <div>
                      <h2 style={{ fontSize: isMobile ? 20 : 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>Groupes Partagés</h2>
                      <p style={{ color: '#7a8aa0', fontSize: 14 }}>Les groupes que d'autres ont partagé avec vous.</p>
                    </div>
                  </div>
                  {(() => {
                    const receivedFolders = sharedFolders.filter(f => f.createdBy !== user.email);
                    return receivedFolders.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '80px 0', border: '2px dashed rgba(255,255,255,0.05)', borderRadius: 32 }}>
                        <Share2 size={40} style={{ margin: '0 auto 12px', color: '#2d3a52' }} />
                        <p style={{ color: '#64748b' }}>Aucun groupe partagé avec vous pour le moment.</p>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: isMobile ? 14 : 20 }}>
                        {receivedFolders.map((folder) => (
                          <SharedFolderCard
                            key={folder.id}
                            folder={folder}
                            localGroups={localGroups}
                            contactCount={(folderContactsMap[folder.id] || []).length}
                            onClick={() => setSelectedFolder(folder)}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* ── Groups Tab ── */}
          {activeTab === 'groups' && (
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
              {selectedGroup ? (
                <GroupDetailView
                  group={localGroups.find(g => g.id === selectedGroup.id) || selectedGroup}
                  groupContacts={allContacts.filter(c => (localGroups.find(g => g.id === selectedGroup.id)?.memberIds || []).includes(c.id))}
                  localContacts={allContacts}
                  onBack={() => setSelectedGroup(null)}
                  onRemoveContact={(contactId) => removeContactFromGroup(selectedGroup.id, contactId)}
                  onAddContact={(contact) => addContactToGroup(selectedGroup.id, contact.id)}
                  onRename={(newName) => renameGroup(selectedGroup.id, newName)}
                  user={user}
                  onShare={() => setSharingGroup(selectedGroup)}
                  sharedFolders={sharedFolders}
                  onRemoveCollaborator={(email) => removeCollaborator(selectedGroup.id, email)}
                  onDeleteGroup={() => { deleteGroupHandler(selectedGroup.id); setSelectedGroup(null); }}
                  onViewContact={(contact) => setViewingContact(contact)}
                />
              ) : (
                <>
                  {isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 2 }}>Mes Groupes</h2>
                          <p style={{ color: '#7a8aa0', fontSize: 12 }}>Vos segments de contacts.</p>
                        </div>
                        <button
                          onClick={() => setIsGroupModalOpen(true)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: '#2563eb', color: '#fff', border: 'none',
                            padding: '9px 14px', borderRadius: 12, fontSize: 12,
                            fontWeight: 700, cursor: 'pointer',
                            boxShadow: '0 4px 16px rgba(37,99,235,0.25)',
                            fontFamily: 'inherit',
                          }}
                        >
                          <Plus size={14} /> Créer
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <div style={{ display: 'flex', background: 'rgba(30,40,64,0.8)', padding: 3, borderRadius: 10, border: '1px solid rgba(255,255,255,0.05)', flex: 1 }}>
                          <FilterButton label="Tout" active={groupShareFilter === 'all'} onClick={() => setGroupShareFilter('all')} />
                          <FilterButton label="Perso" active={groupShareFilter === 'private'} onClick={() => setGroupShareFilter('private')} />
                          <FilterButton label="Partagés" active={groupShareFilter === 'shared'} onClick={() => setGroupShareFilter('shared')} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 32, gap: 16, flexWrap: 'wrap' }}>
                      <div>
                        <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>Mes Groupes</h2>
                        <p style={{ color: '#7a8aa0', fontSize: 14 }}>Vos segments de contacts.</p>
                      </div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', background: 'rgba(30,40,64,0.8)', padding: 3, borderRadius: 14, border: '1px solid rgba(255,255,255,0.05)' }}>
                          <FilterButton label="Tout" active={groupShareFilter === 'all'} onClick={() => setGroupShareFilter('all')} />
                          <FilterButton label="Perso" active={groupShareFilter === 'private'} onClick={() => setGroupShareFilter('private')} />
                          <FilterButton label="Partagés" active={groupShareFilter === 'shared'} onClick={() => setGroupShareFilter('shared')} />
                        </div>
                        <button
                          onClick={() => setIsGroupModalOpen(true)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: '#2563eb', color: '#fff', border: 'none',
                            padding: '12px 20px', borderRadius: 14, fontSize: 13,
                            fontWeight: 700, cursor: 'pointer',
                            boxShadow: '0 4px 20px rgba(37,99,235,0.25)', transition: 'background 0.2s',
                            fontFamily: 'inherit',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#3b82f6')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#2563eb')}
                        >
                          <Plus size={16} /> Nouveau Groupe
                        </button>
                      </div>
                    </div>
                  )}
                  {(() => {
                    const sharedGroupIds = new Set(
                      sharedFolders.filter(f => f.createdBy === user.email).flatMap(f => f.groupIds || [])
                    );
                    const filtered = localGroups.filter(g => {
                      if (groupShareFilter === 'shared') return sharedGroupIds.has(g.id);
                      if (groupShareFilter === 'private') return !sharedGroupIds.has(g.id);
                      return true;
                    });
                    return filtered.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '80px 0', border: '2px dashed rgba(255,255,255,0.05)', borderRadius: 32 }}>
                        <Users size={40} style={{ margin: '0 auto 12px', color: '#2d3a52' }} />
                        <p style={{ color: '#64748b', marginBottom: 8 }}>
                          {localGroups.length === 0 ? 'Aucun groupe créé.' : 'Aucun groupe ne correspond à ce filtre.'}
                        </p>
                        {localGroups.length === 0 && <p style={{ color: '#7a8aa0', fontSize: 13 }}>Créez un groupe pour organiser vos contacts.</p>}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {filtered.map((group) => (
                          <GroupRow
                            key={group.id}
                            group={group}
                            allContacts={allContacts}
                            isShared={sharedGroupIds.has(group.id)}
                            onClick={() => setSelectedGroup(group)}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* ── Settings Tab ── */}
          {activeTab === 'settings' && (
            <div style={{ maxWidth: 700, margin: '0 auto' }}>
              <div style={{ marginBottom: 32 }}>
                <h2 style={{ fontSize: isMobile ? 20 : 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>Paramètres</h2>
                <p style={{ color: '#7a8aa0', fontSize: 14 }}>Configuration de votre compte.</p>
              </div>

              {/* Profil */}
              <div style={{ background: 'rgba(30,40,64,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 20, padding: isMobile ? 20 : 28, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  {user.photoURL ? (
                    <img src={user.photoURL} alt="" style={{ width: 56, height: 56, borderRadius: '50%', border: '2px solid rgba(59,130,246,0.3)' }} />
                  ) : (
                    <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <User size={24} color="#60a5fa" />
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{user.displayName}</div>
                    <div style={{ fontSize: 13, color: '#7a8aa0' }}>{user.email}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'Contacts', value: contacts.length },
                    { label: 'Partagés', value: sharedContacts.length },
                    { label: 'Groupes', value: localGroups.length },
                    { label: 'Partages', value: sharedFolders.length },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 10, padding: '10px 10px', border: '1px solid rgba(255,255,255,0.04)', textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 2 }}>{value}</div>
                      <div style={{ fontSize: 9, color: '#7a8aa0', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Profil étendu */}
              <ProfileEditor user={user} userProfile={userProfile} existingLabels={existingLabels} isMobile={isMobile} onSaveProfile={(uid, data) => dbRef.current.saveUserProfile(uid, data)} />

              {/* QR Code du profil */}
              <Suspense fallback={<div style={{ padding: 20, textAlign: 'center', color: '#7a8aa0' }}>Chargement...</div>}>
                <ProfileQRCode user={user} userProfile={userProfile} isMobile={isMobile} />
              </Suspense>

              {/* Zone danger */}
              <div style={{
                marginTop: 24,
                marginBottom: isMobile ? 40 : 60,
                background: 'rgba(239,68,68,0.04)',
                border: '1px solid rgba(239,68,68,0.15)',
                borderRadius: 20,
                padding: isMobile ? 20 : 28,
              }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fca5a5', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Trash2 size={15} /> Zone danger
                </h3>
                <p style={{ fontSize: 13, color: '#7a8aa0', marginBottom: 18, lineHeight: 1.5 }}>
                  La suppression de votre compte est définitive et irréversible. Tous vos contacts, groupes et données seront effacés.
                </p>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.25)',
                    borderRadius: 12, padding: '11px 20px',
                    color: '#f87171', fontSize: 13, fontWeight: 700,
                    cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.18)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)'; }}
                >
                  <Trash2 size={14} /> Supprimer mon compte et mes données
                </button>
              </div>

            </div>
          )}
        </div>
      </main>

      {/* ── Mobile Bottom Nav ── */}
      {isMobile && (
        <nav
          className="bottom-nav-enter"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 30,
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            background: 'rgba(23,31,46,0.92)',
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            padding: '8px 0 env(safe-area-inset-bottom, 8px)',
          }}
        >
          {[
            { tab: 'all', icon: <LayoutDashboard size={20} />, label: 'Répertoire' },
            { tab: 'groups', icon: <Users size={20} />, label: 'Groupes' },
            { tab: 'sharing', icon: <Share2 size={20} />, label: 'Partagés' },
            {
              tab: 'settings', icon: user.photoURL
                ? <img src={user.photoURL} alt="" style={{ width: 22, height: 22, borderRadius: '50%', border: activeTab === 'settings' ? '2px solid #60a5fa' : '2px solid rgba(255,255,255,0.1)' }} />
                : <User size={20} />, label: 'Profil'
            },
          ].map(({ tab, icon, label }) => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); if (tab === 'sharing') setSelectedFolder(null); }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: activeTab === tab ? '#60a5fa' : '#64748b',
                fontSize: 10,
                fontWeight: activeTab === tab ? 700 : 500,
                padding: '6px 12px',
                borderRadius: 10,
                transition: 'color 0.15s',
                fontFamily: 'inherit',
              }}
            >
              {icon}
              {label}
            </button>
          ))}
        </nav>
      )}

      {/* ── Modals ── */}
      {showCameraPermissionModal && (
        <CameraPermissionModal
          onClose={() => setShowCameraPermissionModal(false)}
          onConfirm={() => { setShowCameraPermissionModal(false); setIsScannerOpen(true); }}
          isMobile={isMobile}
        />
      )}
      {showDeleteConfirm && (
        <DeleteAccountModal
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteAccount}
          isMobile={isMobile}
        />
      )}
      {isFolderModalOpen && (
        <FolderModal onClose={() => setIsFolderModalOpen(false)} onSave={addSharedFolder} groups={localGroups} isMobile={isMobile} />
      )}
      {isGroupModalOpen && (
        <GroupModal onClose={() => setIsGroupModalOpen(false)} onSave={createManualGroup} allContacts={allContacts} existingLabels={existingLabels} isMobile={isMobile} />
      )}
      {sharingGroup && (
        <ShareGroupModal
          onClose={() => setSharingGroup(null)}
          onShare={(email) => handleShareGroup(sharingGroup, email)}
          groupName={sharingGroup.name}
          isMobile={isMobile}
        />
      )}

      {viewingContact && (
        <ContactDetailModal
          contact={viewingContact}
          onClose={() => setViewingContact(null)}
          onEdit={() => { setEditingContact(viewingContact); setIsContactModalOpen(true); setViewingContact(null); }}
          isMobile={isMobile}
        />
      )}

      {isContactModalOpen && (
        <ContactFormModal
          onClose={() => { setIsContactModalOpen(false); setEditingContact(null); }}
          onSave={handleSaveContact}
          editingContact={editingContact}
          existingLabels={existingLabels}
          isMobile={isMobile}
        />
      )}

      {isScannerOpen && (
        <Suspense fallback={<div style={{ position: 'fixed', inset: 0, zIndex: 200, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 32, height: 32, border: '2px solid rgba(129,140,248,0.3)', borderTopColor: '#818cf8', borderRadius: '50%' }} className="animate-spin" /></div>}>
          <QRScannerModal
            onClose={() => setIsScannerOpen(false)}
            onScan={(contactData) => {
              setIsScannerOpen(false);
              setScannedContact(contactData);
            }}
          />
        </Suspense>
      )}

      {scannedContact && (
        <ScannedContactModal
          contact={scannedContact}
          onClose={() => setScannedContact(null)}
          onAdd={() => {
            // Utiliser l'email comme ID deterministe pour eviter les doublons :
            // si on rescanne le meme QR, setDoc ecrasera le contact existant
            // au lieu d'en creer un nouveau.
            const stableId = scannedContact.e
              ? 'qr-' + scannedContact.e.replace(/[^a-zA-Z0-9]/g, '-')
              : null; // null = Date.now() sera utilise comme fallback
            handleSaveContact({
              id: stableId,
              firstName: scannedContact.fn || '',
              lastName: scannedContact.ln || '',
              phone: scannedContact.p || '',
              email: scannedContact.e || '',
              instagram: scannedContact.ig || '',
              labels: ['QR Scan'],
            });
            setScannedContact(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* ── Shared Profile Page (via ?share=uid link) ── */}
      {(sharedProfileData || sharedProfileLoading) && (
        <SharedProfilePage
          profile={sharedProfileData}
          isLoading={sharedProfileLoading}
          isLoggedIn={!!user}
          onAdd={handleAddSharedContact}
          onDecline={() => setSharedProfileData(null)}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};

// ─── FilterButton ────────────────────────────────────────────────────────────
const FilterButton = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: '7px 14px',
      borderRadius: 10,
      fontSize: 12,
      fontWeight: 700,
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      flex: 1,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      background: active ? '#2563eb' : 'transparent',
      color: active ? '#fff' : '#94a3b8',
      boxShadow: active ? '0 2px 12px rgba(37,99,235,0.25)' : 'none',
      fontFamily: 'inherit',
    }}
    onMouseEnter={(e) => !active && (e.currentTarget.style.color = '#f1f5f9')}
    onMouseLeave={(e) => !active && (e.currentTarget.style.color = '#94a3b8')}
  >
    {label}
  </button>
);

// ─── SidebarItem ──────────────────────────────────────────────────────────────
const SidebarItem = ({ icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 14px',
      borderRadius: 14,
      border: active ? '1px solid rgba(59,130,246,0.15)' : '1px solid transparent',
      background: active ? 'rgba(59,130,246,0.08)' : 'transparent',
      cursor: 'pointer',
      transition: 'all 0.15s',
      color: active ? '#60a5fa' : '#94a3b8',
    }}
    onMouseEnter={(e) => !active && (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
    onMouseLeave={(e) => !active && (e.currentTarget.style.background = 'transparent')}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {icon}
      <span style={{ fontSize: 13, fontWeight: active ? 700 : 500 }}>{label}</span>
    </div>
    {badge !== undefined && badge > 0 && (
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          padding: '2px 7px',
          borderRadius: 8,
          background: active ? '#2563eb' : 'rgba(255,255,255,0.07)',
          color: active ? '#fff' : '#94a3b8',
        }}
      >
        {badge}
      </span>
    )}
  </button>
);

// ─── MobileContactRow ─────────────────────────────────────────────────────────
const MobileContactRow = ({
  contact, expanded, onToggle,
  onEdit, onDelete, onViewDetail,
  localGroups = [], onAddToGroup, onNavigateToGroup,
  sharedFolders = [], availableFolders = [], onAddToFolder, onNavigateToFolder,
}) => {
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const groupRef = useRef(null);
  const folderRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (groupRef.current && !groupRef.current.contains(e.target)) setShowGroupPicker(false);
      if (folderRef.current && !folderRef.current.contains(e.target)) setShowFolderPicker(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const initials = contact.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div
      style={{
        background: expanded ? 'rgba(30,40,64,0.7)' : 'rgba(30,40,64,0.3)',
        border: contact.isShared ? '1px solid rgba(59,130,246,0.25)' : '1px solid rgba(255,255,255,0.04)',
        borderRadius: 16,
        overflow: 'hidden',
        transition: 'all 0.2s',
      }}
    >
      {/* Row */}
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 14px', background: 'none', border: 'none',
          cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
        }}
      >
        {contact.photo ? (
          <img src={contact.photo} alt="" style={{ width: 38, height: 38, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <div style={{
            width: 38, height: 38, borderRadius: 12, flexShrink: 0,
            background: contact.isShared ? 'linear-gradient(135deg,#3b82f6,#2563eb)' : 'linear-gradient(135deg,#374151,#1f2937)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800, color: '#fff',
          }}>{initials}</div>
        )}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
            {contact.name}
            {contact.isStarred && <Star size={12} color="#f59e0b" fill="#f59e0b" style={{ flexShrink: 0 }} />}
          </div>
          <div style={{ fontSize: 11, color: '#7a8aa0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {contact.phone || contact.email || contact.instagram || '—'}
          </div>
        </div>
        {contact.labels && contact.labels.length > 0 && (
          <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 6, background: 'rgba(168,85,247,0.1)', color: '#c084fc', fontWeight: 700, flexShrink: 0 }}>
            {contact.labels[0]}{contact.labels.length > 1 ? ` +${contact.labels.length - 1}` : ''}
          </span>
        )}
        {contact.isShared && <ShieldCheck size={12} color="#60a5fa" style={{ flexShrink: 0 }} />}
        <ChevronDown size={14} color="#64748b" style={{ flexShrink: 0, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }} />
      </button>

      {/* Expanded actions */}
      {expanded && (
        <div style={{
          padding: '0 14px 12px',
          display: 'flex', alignItems: 'center', gap: 8,
          borderTop: '1px solid rgba(255,255,255,0.04)',
          paddingTop: 10,
          animation: 'fade-in 0.15s ease-out forwards',
        }}>
          {/* View detail */}
          <button onClick={onViewDetail} title="Voir la fiche" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 8, borderRadius: 10, background: 'rgba(59,130,246,0.1)',
            border: '1px solid rgba(59,130,246,0.2)', color: '#60a5fa', cursor: 'pointer',
          }}><Eye size={15} /></button>

          {/* Edit */}
          {!contact.isShared && onEdit && (
            <button onClick={onEdit} title="Modifier" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 8, borderRadius: 10, background: 'rgba(250,204,21,0.1)',
              border: '1px solid rgba(250,204,21,0.2)', color: '#facc15', cursor: 'pointer',
            }}><Pencil size={15} /></button>
          )}

          {/* Delete */}
          {!contact.isShared && onDelete && (
            <button onClick={() => { if (confirm('Supprimer ce contact ?')) onDelete(); }} title="Supprimer" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 8, borderRadius: 10, background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer',
            }}><Trash2 size={15} /></button>
          )}

          <div style={{ flex: 1 }} />

          {/* Group picker */}
          <div ref={groupRef} style={{ position: 'relative' }}>
            {showGroupPicker && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 8px)', right: 0, width: 200,
                background: 'rgba(25,35,55,0.98)', backdropFilter: 'blur(16px)',
                border: '1px solid rgba(99,102,241,0.3)', borderRadius: 14,
                boxShadow: '0 16px 48px rgba(0,0,0,0.5)', zIndex: 50, overflow: 'hidden',
              }}>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 10, fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Groupes</div>
                <div style={{ padding: 6, maxHeight: 180, overflowY: 'auto' }}>
                  {localGroups.map(g => {
                    const alreadyIn = g.memberIds?.includes(contact.id);
                    return (
                      <button key={g.id} onClick={() => { if (!alreadyIn) { onAddToGroup(g.id); setShowGroupPicker(false); } else if (onNavigateToGroup) { setShowGroupPicker(false); onNavigateToGroup(g); } }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: 'none', border: 'none', borderRadius: 8, color: alreadyIn ? '#818cf8' : '#cbd5e1', fontSize: 12, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Users size={10} style={{ opacity: 0.5, flexShrink: 0 }} />{g.name}</span>
                        {alreadyIn ? <Check size={11} color="#4ade80" /> : <Plus size={10} color="#64748b" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <button onClick={() => setShowGroupPicker(!showGroupPicker)} title="Groupes" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 8, borderRadius: 10, background: showGroupPicker ? '#4f46e5' : 'rgba(99,102,241,0.1)',
              border: '1px solid rgba(99,102,241,0.2)', color: showGroupPicker ? '#fff' : '#818cf8', cursor: 'pointer',
            }}><Users size={15} /></button>
          </div>

          {/* Shared folder picker */}
          {availableFolders.length > 0 && (
            <div ref={folderRef} style={{ position: 'relative' }}>
              {showFolderPicker && (
                <div style={{
                  position: 'absolute', bottom: 'calc(100% + 8px)', right: 0, width: 200,
                  background: 'rgba(25,35,55,0.98)', backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(59,130,246,0.3)', borderRadius: 14,
                  boxShadow: '0 16px 48px rgba(0,0,0,0.5)', zIndex: 50, overflow: 'hidden',
                }}>
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 10, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Partagés</div>
                  <div style={{ padding: 6, maxHeight: 180, overflowY: 'auto' }}>
                    {availableFolders.map(f => {
                      const alreadyIn = sharedFolders.some(sf => sf.id === f.id);
                      return (
                        <button key={f.id} onClick={() => { if (!alreadyIn) { onAddToFolder(f.id); setShowFolderPicker(false); } else if (onNavigateToFolder) { setShowFolderPicker(false); onNavigateToFolder(f); } }}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: 'none', border: 'none', borderRadius: 8, color: alreadyIn ? '#60a5fa' : '#cbd5e1', fontSize: 12, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Folder size={10} style={{ opacity: 0.5, flexShrink: 0 }} />{f.name}</span>
                          {alreadyIn ? <Check size={11} color="#4ade80" /> : <Plus size={10} color="#64748b" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <button onClick={() => setShowFolderPicker(!showFolderPicker)} title="Groupes partagés" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                padding: '8px 10px', borderRadius: 10, background: showFolderPicker ? '#2563eb' : 'rgba(59,130,246,0.1)',
                border: '1px solid rgba(59,130,246,0.2)', color: showFolderPicker ? '#fff' : '#60a5fa', cursor: 'pointer',
              }}>
                <FolderPlus size={15} />
                {sharedFolders.length > 0 && <span style={{ fontSize: 10, fontWeight: 800 }}>{sharedFolders.length}</span>}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── ContactCard ──────────────────────────────────────────────────────────────
const ContactCard = ({
  contact,
  sharedFolders = [],
  availableFolders = [],
  onAddToFolder,

  localGroups = [],
  onAddToGroup,
  onNavigateToGroup,
  onNavigateToFolder,
  onEdit,
  onDelete,
  onClick,
}) => {
  const [showDropUp, setShowDropUp] = useState(false);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const menuRef = useRef(null);
  const folderPickerRef = useRef(null);
  const groupPickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowDropUp(false);
      if (folderPickerRef.current && !folderPickerRef.current.contains(e.target)) setShowFolderPicker(false);
      if (groupPickerRef.current && !groupPickerRef.current.contains(e.target)) setShowGroupPicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = contact.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      style={{
        background: contact.isShared
          ? 'rgba(30,40,64,0.6)'
          : 'rgba(30,40,64,0.4)',
        border: contact.isShared
          ? '1px solid rgba(59,130,246,0.35)'
          : '1px solid rgba(255,255,255,0.05)',
        borderRadius: 24,
        padding: 22,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'transform 0.2s, box-shadow 0.2s',
        position: 'relative',
        overflow: 'visible',
        backdropFilter: 'blur(8px)',
        boxShadow: contact.isShared ? '0 0 20px rgba(59,130,246,0.08)' : 'none',
        cursor: onClick ? 'pointer' : 'default',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = contact.isShared
          ? '0 8px 32px rgba(59,130,246,0.15)'
          : '0 8px 32px rgba(0,0,0,0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = contact.isShared ? '0 0 20px rgba(59,130,246,0.08)' : 'none';
      }}
      onClick={onClick}
    >
      {contact.isShared && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: '#2563eb',
            borderRadius: '0 24px 0 12px',
            padding: '6px 10px',
          }}
        >
          <ShieldCheck size={12} color="#fff" />
        </div>
      )}

      {/* Top */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          {contact.photo ? (
            <img
              src={contact.photo}
              alt={contact.name}
              style={{
                width: 52,
                height: 52,
                borderRadius: 16,
                objectFit: 'cover',
                border: contact.isShared ? '2px solid rgba(59,130,246,0.4)' : '2px solid rgba(255,255,255,0.05)',
              }}
            />
          ) : (
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 16,
                background: contact.isShared
                  ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                  : 'linear-gradient(135deg, #374151, #1f2937)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 800,
                color: '#fff',
              }}
            >
              {initials}
            </div>
          )}
          <div style={{ overflow: 'hidden', flex: 1 }}>
            <h3
              style={{
                fontWeight: 700,
                fontSize: 15,
                color: '#f1f5f9',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {contact.name}
              {contact.isStarred && <Star size={14} color="#f59e0b" fill="#f59e0b" style={{ flexShrink: 0 }} />}
            </h3>
            <p
              style={{
                fontSize: 12,
                color: '#7a8aa0',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginTop: 2,
              }}
            >
              {contact.email || contact.phone || '—'}
            </p>
          </div>
        </div>
        {/* Labels & Instagram */}
        {((contact.labels && contact.labels.length > 0) || contact.instagram) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
            {contact.labels?.map(l => (
              <span
                key={l}
                style={{
                  padding: '3px 9px',
                  background: 'rgba(168,85,247,0.08)',
                  border: '1px solid rgba(168,85,247,0.2)',
                  borderRadius: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#a855f7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Tag size={9} /> {l}
              </span>
            ))}
            {contact.instagram && (
              <a
                href={`https://www.instagram.com/${contact.instagram.replace(/^@/, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  padding: '3px 10px',
                  background: 'rgba(236,72,153,0.08)',
                  border: '1px solid rgba(236,72,153,0.2)',
                  borderRadius: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#ec4899',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(236,72,153,0.2)'; e.currentTarget.style.borderColor = 'rgba(236,72,153,0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(236,72,153,0.08)'; e.currentTarget.style.borderColor = 'rgba(236,72,153,0.2)'; }}
              >
                <AtSign size={10} />
                {contact.instagram}
              </a>
            )}
          </div>
        )}
      </div>

      {/* Bottom */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          paddingTop: 14,
          marginTop: 8,
          gap: 8,
        }}
      >
        {/* Edit / Delete for personal contacts */}
        {!contact.isShared && onEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            title="Modifier"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 7, borderRadius: 10,
              background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.2)',
              color: '#facc15', cursor: 'pointer', transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#ca8a04'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(250,204,21,0.1)'; e.currentTarget.style.color = '#facc15'; }}
          >
            <Pencil size={14} />
          </button>
        )}
        {!contact.isShared && onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); if (confirm('Supprimer ce contact ?')) onDelete(); }}
            title="Supprimer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 7, borderRadius: 10,
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
              color: '#ef4444', cursor: 'pointer', transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#ef4444'; }}
          >
            <Trash2 size={14} />
          </button>
        )}

        <div style={{ flex: 1 }} />
        {contact.isShared && contact.sharedBy && (
          <div style={{ fontSize: 10, color: 'rgba(96,165,250,0.7)', display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
            <Globe size={10} />
            {contact.sharedBy.split('@')[0]}
          </div>
        )}

        {/* Add to group (left of AI button) */}
        <div ref={groupPickerRef} style={{ position: 'relative' }}>
          {showGroupPicker && (
            <div
              style={{
                position: 'absolute',
                bottom: 'calc(100% + 10px)',
                right: 0,
                width: 220,
                background: 'rgba(30,40,60,0.97)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(99,102,241,0.3)',
                borderRadius: 16,
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                zIndex: 50,
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <p style={{ fontSize: 10, fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Ajouter au groupe
                </p>
              </div>
              {localGroups.length > 0 ? (
                <div style={{ padding: 8, maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {localGroups.map((g) => {
                    const alreadyIn = g.memberIds?.includes(contact.id);
                    return (
                      <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!alreadyIn) {
                              onAddToGroup(g.id);
                              setShowGroupPicker(false);
                            } else if (onNavigateToGroup) {
                              setShowGroupPicker(false);
                              onNavigateToGroup(g);
                            }
                          }}
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            borderRadius: 8,
                            color: alreadyIn ? '#818cf8' : '#cbd5e1',
                            fontSize: 12,
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'all 0.15s',
                            fontFamily: 'inherit',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = alreadyIn ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.1)';
                            if (!alreadyIn) e.currentTarget.style.color = '#818cf8';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            if (!alreadyIn) e.currentTarget.style.color = '#cbd5e1';
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Users size={11} style={{ flexShrink: 0, opacity: 0.5 }} />
                            {g.name}
                          </span>
                          {alreadyIn
                            ? <Check size={12} color="#4ade80" style={{ flexShrink: 0 }} />
                            : <Plus size={11} color="#64748b" style={{ flexShrink: 0 }} />
                          }
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ padding: '16px 20px', textAlign: 'center' }}>
                  <p style={{ fontSize: 11, color: '#7a8aa0', lineHeight: 1.5, margin: 0 }}>
                    Aucun groupe créé.<br />
                    Créez-en un d'abord dans l'onglet <strong>Mes Groupes</strong>.
                  </p>
                </div>
              )}
            </div>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); setShowGroupPicker(!showGroupPicker); }}
            title="Ajouter au groupe"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 7,
              borderRadius: 10,
              background: 'rgba(99,102,241,0.1)',
              border: '1px solid rgba(99,102,241,0.2)',
              color: '#818cf8',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#4f46e5';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.boxShadow = '0 0 14px rgba(99,102,241,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(99,102,241,0.1)';
              e.currentTarget.style.color = '#818cf8';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Users size={14} />
          </button>
        </div>


        {/* Add to shared folder */}
        {availableFolders.length > 0 && (
          <div ref={folderPickerRef} style={{ position: 'relative' }}>
            {showFolderPicker && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 'calc(100% + 10px)',
                  right: 0,
                  width: 220,
                  background: 'rgba(30,40,60,0.97)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(59,130,246,0.3)',
                  borderRadius: 16,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                  zIndex: 50,
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    Ajouter au groupe
                  </p>
                </div>
                <div style={{ padding: 8 }}>
                  {availableFolders.map((f) => {
                    const alreadyIn = sharedFolders.some(sf => sf.id === f.id);
                    return (
                      <button
                        key={f.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!alreadyIn) {
                            onAddToFolder(f.id);
                            setShowFolderPicker(false);
                          } else if (onNavigateToFolder) {
                            setShowFolderPicker(false);
                            onNavigateToFolder(f);
                          }
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                          padding: '7px 8px',
                          borderRadius: 8,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: alreadyIn ? '#60a5fa' : '#f1f5f9',
                          fontSize: 13,
                          textAlign: 'left',
                          transition: 'background 0.15s',
                          fontFamily: 'inherit',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = alreadyIn ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.1)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                          <Folder size={12} color={alreadyIn ? '#60a5fa' : '#94a3b8'} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.name}
                          </span>
                        </div>
                        {alreadyIn
                          ? <Check size={12} color="#4ade80" />
                          : <Plus size={11} color="#64748b" />
                        }
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setShowFolderPicker(!showFolderPicker); }}
              title="Ajouter à un groupe partagé"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                padding: '7px 10px',
                borderRadius: 10,
                background: showFolderPicker ? '#2563eb' : 'rgba(59,130,246,0.1)',
                border: showFolderPicker ? '1px solid #60a5fa' : '1px solid rgba(59,130,246,0.2)',
                color: showFolderPicker ? '#fff' : '#60a5fa',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => !showFolderPicker && (e.currentTarget.style.background = 'rgba(59,130,246,0.2)')}
              onMouseLeave={(e) => !showFolderPicker && (e.currentTarget.style.background = 'rgba(59,130,246,0.1)')}
            >
              <FolderPlus size={14} />
              {sharedFolders.length > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 800, color: '#60a5fa',
                  minWidth: 14, textAlign: 'center',
                }}>{sharedFolders.length}</span>
              )}
            </button>
          </div>
        )}


      </div>
    </div>
  );
};

// ─── FolderDetailView ────────────────────────────────────────────────────────
const FolderDetailView = ({ folder, folderContacts, onBack }) => {
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12, padding: '8px 14px', color: '#9ca3af',
            fontSize: 13, cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#9ca3af'; }}
        >
          <ArrowRight size={14} style={{ transform: 'rotate(180deg)' }} /> Groupes partagés
        </button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6 }}>{folder.name}</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {folder.members?.map(email => (
              <span key={email} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '3px 10px', background: 'rgba(59,130,246,0.1)',
                border: '1px solid rgba(59,130,246,0.2)', borderRadius: 99,
                fontSize: 11, color: '#60a5fa',
              }}>
                <Mail size={9} /> {email}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Contacts in folder */}
      {folderContacts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0', border: '2px dashed rgba(255,255,255,0.05)', borderRadius: 32 }}>
          <Folder size={40} style={{ margin: '0 auto 12px', color: '#2d3a52' }} />
          <p style={{ color: '#64748b', marginBottom: 8 }}>Ce groupe est vide.</p>
        </div>
      ) : (
        <>
          <p style={{ fontSize: 13, color: '#7a8aa0', marginBottom: 20 }}>
            {folderContacts.length} contact{folderContacts.length > 1 ? 's' : ''} dans ce groupe
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 16 }}>
            {folderContacts.map(contact => (
              <div
                key={contact.id}
                style={{
                  background: 'rgba(30,40,64,0.6)', border: '1px solid rgba(59,130,246,0.2)',
                  borderRadius: 20, padding: 18, display: 'flex', alignItems: 'center',
                  gap: 14, backdropFilter: 'blur(8px)', transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.5)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.2)')}
              >
                {contact.photo
                  ? <img src={contact.photo} alt={contact.name} style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
                  : <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{contact.name[0]}</div>
                }
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.name}</p>
                  <p style={{ fontSize: 12, color: '#7a8aa0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.email}</p>
                  {contact.sharedBy && (
                    <p style={{ fontSize: 10, color: 'rgba(96,165,250,0.6)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Globe size={8} /> {contact.sharedBy.split('@')[0]}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── SharedFolderCard ─────────────────────────────────────────────────────────
const SharedFolderCard = ({ folder, localGroups, contactCount = 0, onClick }) => {
  const linkedGroups = localGroups.filter((g) => folder.groupIds?.includes(g.id));
  return (
    <div
      onClick={onClick}
      style={{
        background: 'rgba(30,40,64,0.5)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 24, padding: 24,
        transition: 'border-color 0.2s, transform 0.2s',
        backdropFilter: 'blur(8px)',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(59,130,246,0.35)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ padding: 14, background: 'rgba(59,130,246,0.1)', borderRadius: 16 }}>
          <Folder size={28} color="#60a5fa" />
        </div>
        <span style={{ fontSize: 11, color: '#7a8aa0', background: 'rgba(255,255,255,0.05)', padding: '3px 10px', borderRadius: 8 }}>
          {contactCount} contact{contactCount !== 1 ? 's' : ''}
        </span>
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>{folder.name}</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: linkedGroups.length > 0 ? 12 : 0 }}>
        {folder.members?.map((email) => (
          <div key={email} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)', borderRadius: 99,
            fontSize: 11, color: '#94a3b8',
          }}>
            <Mail size={10} /> {email}
          </div>
        ))}
      </div>
      {linkedGroups.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 12 }}>
          {linkedGroups.map((g) => (
            <div key={g.id} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', background: 'rgba(99,102,241,0.06)',
              border: '1px solid rgba(99,102,241,0.15)', borderRadius: 99,
              fontSize: 11, color: '#818cf8',
            }}>
              <Users size={10} /> {g.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ─── GroupRow ─────────────────────────────────────────────────────────────────
const GroupRow = ({ group, allContacts, isShared, onClick }) => {
  const members = allContacts.filter((c) => group.memberIds.includes(c.id));
  return (
    <div
      onClick={onClick}
      style={{
        background: isShared ? 'rgba(30,40,64,0.6)' : 'rgba(30,40,64,0.5)',
        border: isShared ? '1px solid rgba(59,130,246,0.2)' : '1px solid rgba(255,255,255,0.06)',
        borderRadius: 20,
        padding: '18px 22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'background 0.2s',
        backdropFilter: 'blur(8px)',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(30,40,64,0.7)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = isShared ? 'rgba(30,40,64,0.6)' : 'rgba(30,40,64,0.5)')}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, overflow: 'hidden', minWidth: 0 }}>
        <div style={{ width: 44, height: 44, borderRadius: 14, background: isShared ? 'rgba(59,130,246,0.1)' : 'rgba(99,102,241,0.1)', border: isShared ? '1px solid rgba(59,130,246,0.25)' : '1px solid rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Users size={20} color={isShared ? '#60a5fa' : '#818cf8'} />
        </div>
        <div style={{ overflow: 'hidden', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h4 style={{ fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</h4>
            {isShared && (
              <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 6, background: 'rgba(59,130,246,0.1)', color: '#60a5fa', fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 3 }}>
                <Share2 size={8} /> Partagé
              </span>
            )}
          </div>
          <p style={{ fontSize: 12, color: '#7a8aa0' }}>{members.length} membre{members.length > 1 ? 's' : ''}</p>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <div style={{ display: 'flex' }}>
          {members.slice(0, 3).map((m) => (
            <div
              key={m.id}
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: '#2d3a52',
                border: '2px solid #0d1117',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                marginLeft: -8,
                color: '#f1f5f9',
              }}
            >
              {m.name.charAt(0)}
            </div>
          ))}
          {members.length > 3 && (
            <div style={{
              width: 30, height: 30, borderRadius: '50%', background: 'rgba(99,102,241,0.15)',
              border: '2px solid #0d1117', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, marginLeft: -8, color: '#818cf8',
            }}>+{members.length - 3}</div>
          )}
        </div>
        <ArrowRight size={16} color="#64748b" />
      </div>
    </div>
  );
};


// ─── GroupDetailView ──────────────────────────────────────────────────────────
const GroupDetailView = ({ group, groupContacts, localContacts, onBack, onRemoveContact, onAddContact, onRename, user, onShare, sharedFolders = [], onRemoveCollaborator, onDeleteGroup, onViewContact }) => {
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(group.name);
  const [prevGroup, setPrevGroup] = useState(group);

  if (group.id !== prevGroup.id || group.name !== prevGroup.name) {
    setPrevGroup(group);
    setEditName(group.name);
  }

  const memberIds = useMemo(() => new Set(group.memberIds || []), [group.memberIds]);
  const addable = useMemo(() => localContacts.filter(c =>
    !memberIds.has(c.id) &&
    (c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.email && c.email.toLowerCase().includes(search.toLowerCase())))
  ), [localContacts, memberIds, search]);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        {/* Back button — standalone */}
        <button
          onClick={onBack}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', color: '#7a8aa0',
            fontSize: 13, cursor: 'pointer', padding: '4px 0', marginBottom: 20,
            fontFamily: 'inherit', transition: 'color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#f1f5f9')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#7a8aa0')}
        >
          <ArrowRight size={14} style={{ transform: 'rotate(180deg)' }} /> Groupes
        </button>

        {/* Title */}
        {isEditingName ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <input
              autoFocus
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (editName.trim()) { onRename(editName.trim()); setIsEditingName(false); }
                } else if (e.key === 'Escape') {
                  setEditName(group.name); setIsEditingName(false);
                }
              }}
              style={{
                background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 12, padding: '10px 16px', color: '#f1f5f9',
                fontSize: 26, fontWeight: 800, outline: 'none', fontFamily: 'inherit',
                flex: 1, maxWidth: 500,
              }}
            />
            <button
              onClick={() => { if (editName.trim()) { onRename(editName.trim()); setIsEditingName(false); } }}
              style={{
                background: '#4f46e5', border: 'none', color: '#fff', borderRadius: 10,
                padding: '10px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 700,
              }}
            >Sauver</button>
            <button
              onClick={() => { setEditName(group.name); setIsEditingName(false); }}
              style={{
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                color: '#9ca3af', borderRadius: 10, padding: '10px 18px', cursor: 'pointer', fontSize: 13,
              }}
            >Annuler</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <h2 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', margin: 0 }}>{group.name}</h2>
            <button
              onClick={() => setIsEditingName(true)}
              title="Modifier le nom"
              style={{
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10, padding: 8, color: '#64748b', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#64748b'; }}
            >
              <Pencil size={15} />
            </button>
          </div>
        )}

        {/* Meta info */}
        <p style={{ fontSize: 14, color: '#7a8aa0', margin: '0 0 12px 0' }}>
          {groupContacts.length} contact{groupContacts.length > 1 ? 's' : ''} dans ce groupe
        </p>

        {/* Collaborateurs */}
        {(() => {
          const linkedFolder = user && sharedFolders.find(f => f.groupIds?.includes(group.id) && f.createdBy === user.email);
          if (!linkedFolder) return null;
          const collaborators = linkedFolder.members.filter(m => m.toLowerCase() !== user.email.toLowerCase());
          if (collaborators.length === 0) return null;
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: '#7a8aa0', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Share2 size={11} /> Partagé avec
              </span>
              {collaborators.map(email => (
                <span key={email} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '4px 12px', background: 'rgba(59,130,246,0.1)',
                  border: '1px solid rgba(59,130,246,0.2)', borderRadius: 99,
                  fontSize: 12, color: '#60a5fa',
                }}>
                  <Mail size={10} /> {email}
                  <button
                    onClick={() => onRemoveCollaborator && onRemoveCollaborator(email)}
                    title={`Retirer ${email}`}
                    style={{
                      background: 'none', border: 'none', color: '#f87171',
                      cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center',
                      marginLeft: 2,
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#f87171')}
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          );
        })()}

        {/* Separator */}
        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '20px 0' }} />

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {user && (
            <button
              onClick={onShare}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(59,130,246,0.1)',
                border: '1px solid rgba(59,130,246,0.2)',
                borderRadius: 12, padding: '10px 20px',
                color: '#60a5fa',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s', fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(59,130,246,0.2)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(59,130,246,0.1)')}
            >
              <Share2 size={14} /> Partager
            </button>
          )}
          {user && onDeleteGroup && (
            <button
              onClick={() => { if (window.confirm(`Supprimer le groupe « ${group.name} » et son partage associé ?`)) onDeleteGroup(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.15)',
                borderRadius: 12, padding: '10px 20px',
                color: '#f87171',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s', fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239,68,68,0.18)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(239,68,68,0.08)')}
            >
              <Trash2 size={14} /> Supprimer
            </button>
          )}
          <button
            onClick={() => setShowAdd(!showAdd)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: showAdd ? '#4f46e5' : 'rgba(79,70,229,0.12)',
              border: showAdd ? '1px solid #4f46e5' : '1px solid rgba(99,102,241,0.2)',
              borderRadius: 12, padding: '10px 20px',
              color: showAdd ? '#fff' : '#818cf8',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s', fontFamily: 'inherit',
            }}
          >
            <UserPlus size={14} /> Ajouter un contact
          </button>
        </div>
      </div>

      {/* Add contact panel */}
      {showAdd && (
        <div style={{
          background: 'rgba(30,40,64,0.7)', border: '1px solid rgba(99,102,241,0.2)',
          borderRadius: 20, padding: 20, marginBottom: 28,
        }}>
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <Search size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#7a8aa0', pointerEvents: 'none' }} />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher dans vos contacts..."
              style={{
                width: '100%', background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12,
                padding: '10px 16px 10px 40px', color: '#f1f5f9', fontSize: 13,
                outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
              }}
            />
          </div>
          {localContacts.length === 0 ? (
            <p style={{ fontSize: 13, color: '#7a8aa0', textAlign: 'center', padding: '12px 0' }}>Aucun contact disponible.</p>
          ) : addable.length === 0 ? (
            <p style={{ fontSize: 13, color: '#7a8aa0', textAlign: 'center', padding: '12px 0' }}>Tous vos contacts sont déjà dans ce groupe.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
              {addable.slice(0, 20).map(c => (
                <button
                  key={c.id}
                  onClick={() => { onAddContact(c); setSearch(''); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '8px 12px', borderRadius: 10,
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#f1f5f9', textAlign: 'left', transition: 'background 0.15s',
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(99,102,241,0.1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  {c.photo
                    ? <img src={c.photo} alt={c.name} style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover' }} />
                    : <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#818cf8' }}>{c.name[0]}</div>
                  }
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: '#7a8aa0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email || c.phone || ''}</div>
                  </div>
                  <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
                    <Plus size={14} color="#818cf8" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Contacts in group */}
      {groupContacts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0', border: '2px dashed rgba(255,255,255,0.05)', borderRadius: 32 }}>
          <Users size={40} style={{ margin: '0 auto 12px', color: '#2d3a52' }} />
          <p style={{ color: '#64748b', marginBottom: 8 }}>Ce groupe est vide.</p>
          <p style={{ color: '#7a8aa0', fontSize: 13 }}>Ajoutez des contacts depuis le Répertoire ou via le bouton ci-dessus.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 16 }}>
          {groupContacts.map(contact => (
            <div
              key={contact.id}
              onClick={() => onViewContact && onViewContact(contact)}
              style={{
                background: 'rgba(30,40,64,0.6)', border: '1px solid rgba(99,102,241,0.2)',
                borderRadius: 20, padding: 18, display: 'flex', alignItems: 'center',
                gap: 14, backdropFilter: 'blur(8px)', transition: 'border-color 0.2s',
                position: 'relative', cursor: 'pointer',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)';
                const btn = e.currentTarget.querySelector('[data-remove-btn]');
                if (btn) { btn.style.opacity = '1'; btn.style.pointerEvents = 'auto'; }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)';
                const btn = e.currentTarget.querySelector('[data-remove-btn]');
                if (btn) { btn.style.opacity = '0'; btn.style.pointerEvents = 'none'; }
              }}
            >
              {contact.photo
                ? <img src={contact.photo} alt={contact.name} style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#4f46e5,#312e81)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{contact.name[0]}</div>
              }
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.name}</p>
                <p style={{ fontSize: 12, color: '#7a8aa0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.email || contact.phone || ''}</p>
              </div>
              <button
                data-remove-btn
                onClick={(e) => { e.stopPropagation(); onRemoveContact(contact.id); }}
                title="Retirer du groupe"
                style={{
                  padding: 7, borderRadius: 8, background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.2)', color: '#f87171',
                  cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, pointerEvents: 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ─── GroupModal ───────────────────────────────────────────────────────────────
const GroupModal = ({ onClose, onSave, allContacts, existingLabels = [], isMobile }) => {
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const filteredContacts = allContacts.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.email && c.email.toLowerCase().includes(search.toLowerCase()))
  );

  const toggleMember = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => {
      const next = prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag];
      // Auto-select/deselect contacts with this tag
      const contactsWithTag = allContacts.filter(c => c.labels?.includes(tag)).map(c => c.id);
      if (next.includes(tag)) {
        setSelectedIds(ids => [...new Set([...ids, ...contactsWithTag])]);
      } else {
        // Only remove contacts that don't belong to any other selected tag
        const otherTags = next;
        const protectedIds = new Set(allContacts.filter(c => c.labels?.some(l => otherTags.includes(l))).map(c => c.id));
        setSelectedIds(ids => ids.filter(id => protectedIds.has(id) || !contactsWithTag.includes(id)));
      }
      // Auto-fill name when exactly 1 tag selected
      if (next.length === 1) {
        setName(prev => (!prev || prev === tag || existingLabels.includes(prev)) ? next[0] : prev);
      } else if (next.length === 0) {
        setName(prev => existingLabels.includes(prev) ? '' : prev);
      }
      return next;
    });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
      padding: isMobile ? 0 : 20, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
    }}>
      <div style={{
        background: '#1e2840', border: '1px solid rgba(255,255,255,0.1)',
        width: '100%', maxWidth: isMobile ? '100%' : 560,
        borderRadius: isMobile ? '20px 20px 0 0' : 28,
        padding: isMobile ? '24px 16px' : '36px 32px',
        boxShadow: '0 40px 100px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', gap: 20,
        maxHeight: isMobile ? '98vh' : '85vh', minHeight: isMobile ? '98vh' : undefined, overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 22, fontWeight: 800 }}>Nouveau Groupe</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 6 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}>
            <X size={22} />
          </button>
        </div>

        {/* Nom */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nom du groupe</label>
          <input
            autoFocus value={name} onChange={e => setName(e.target.value)}
            placeholder="Ex : Équipe production"
            style={{
              width: '100%', background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14,
              padding: '13px 16px', color: '#f1f5f9', fontSize: 14,
              outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
            }}
            onFocus={e => (e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)')}
            onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)')}
          />
        </div>

        {/* Tags */}
        {existingLabels.length > 0 && (
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Ajouter par tag
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {existingLabels.map(tag => {
                const isActive = selectedTags.includes(tag);
                const count = allContacts.filter(c => c.labels?.includes(tag)).length;
                return (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    style={{
                      padding: '6px 12px', borderRadius: 10,
                      background: isActive ? 'rgba(168,85,247,0.2)' : 'rgba(168,85,247,0.06)',
                      border: `1px solid ${isActive ? 'rgba(168,85,247,0.5)' : 'rgba(168,85,247,0.15)'}`,
                      color: isActive ? '#c084fc' : '#94a3b8',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                      transition: 'all 0.15s', fontFamily: 'inherit',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(168,85,247,0.4)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = isActive ? 'rgba(168,85,247,0.5)' : 'rgba(168,85,247,0.15)'; }}
                  >
                    <Tag size={10} />
                    {tag}
                    <span style={{ fontSize: 10, color: isActive ? '#a78bfa' : '#64748b' }}>({count})</span>
                    {isActive && <Check size={11} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}


        {/* Membres */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Membres — <span style={{ color: '#818cf8' }}>{selectedIds.length} sélectionné{selectedIds.length > 1 ? 's' : ''}</span>
          </label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#7a8aa0', pointerEvents: 'none' }} />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un contact..."
              style={{
                width: '100%', background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12,
                padding: '10px 16px 10px 40px', color: '#f1f5f9', fontSize: 13,
                outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 260, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {filteredContacts.slice(0, 50).map(c => {
              const selected = selectedIds.includes(c.id);
              return (
                <button key={c.id} onClick={() => toggleMember(c.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '8px 12px', borderRadius: 10,
                    background: selected ? 'rgba(99,102,241,0.12)' : 'none',
                    border: selected ? '1px solid rgba(99,102,241,0.3)' : '1px solid transparent',
                    cursor: 'pointer', color: '#f1f5f9', textAlign: 'left',
                    transition: 'all 0.15s', fontFamily: 'inherit',
                  }}
                  onMouseEnter={e => !selected && (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                  onMouseLeave={e => !selected && (e.currentTarget.style.background = 'none')}
                >
                  {c.photo
                    ? <img src={c.photo} alt={c.name} style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={{ width: 32, height: 32, borderRadius: 8, background: selected ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: selected ? '#818cf8' : '#94a3b8', flexShrink: 0 }}>{c.name[0]}</div>
                  }
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: '#7a8aa0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email || c.phone || ''}</div>
                  </div>
                  {selected && <Check size={14} color="#818cf8" style={{ flexShrink: 0 }} />}
                </button>
              );
            })}
            {filteredContacts.length === 0 && <p style={{ fontSize: 13, color: '#7a8aa0', textAlign: 'center', padding: '20px 0' }}>Aucun contact trouvé.</p>}
          </div>
        </div>

        {/* Bouton créer */}
        <button
          disabled={!name.trim()}
          onClick={() => onSave({ name: name.trim(), memberIds: selectedIds })}
          style={{
            width: '100%', padding: '15px 20px',
            background: name.trim() ? '#4f46e5' : 'rgba(79,70,229,0.3)',
            border: 'none', borderRadius: 16, color: '#fff',
            fontSize: 14, fontWeight: 700,
            cursor: name.trim() ? 'pointer' : 'not-allowed',
            boxShadow: name.trim() ? '0 4px 20px rgba(79,70,229,0.3)' : 'none',
            transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
          onMouseEnter={e => name.trim() && (e.currentTarget.style.background = '#6366f1')}
          onMouseLeave={e => name.trim() && (e.currentTarget.style.background = '#4f46e5')}
        >
          <Users size={16} /> Créer le groupe ({selectedIds.length} membre{selectedIds.length > 1 ? 's' : ''})
        </button>
      </div>
    </div>
  );
};

// ─── ContactFormModal ─────────────────────────────────────────────────────────
const ContactFormModal = ({ onClose, onSave, editingContact, existingLabels = [], isMobile }) => {
  const [firstName, setFirstName] = useState(editingContact?.firstName || '');
  const [lastName, setLastName] = useState(editingContact?.lastName || '');
  const [phone, setPhone] = useState(editingContact?.phone || '');
  const [email, setEmail] = useState(editingContact?.email || '');
  const [instagram, setInstagram] = useState(editingContact?.instagram || '');
  const [labels, setLabels] = useState(editingContact?.labels || []);
  const [labelInput, setLabelInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const labelInputRef = useRef(null);
  const isEditing = !!editingContact;

  const canSave = firstName.trim() || lastName.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSave) return;
    // Flush any pending tag in the input field before saving
    const finalLabels = [...labels];
    if (labelInput.trim() && !finalLabels.includes(labelInput.trim())) {
      finalLabels.push(labelInput.trim());
    }
    onSave({
      id: editingContact?.id,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      email: email.trim(),
      instagram: instagram.trim(),
      labels: finalLabels,
    });
  };

  const inputStyle = {
    width: '100%',
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    padding: '14px 18px',
    fontSize: 14,
    color: '#f1f5f9',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: '#7a8aa0',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 20,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          background: '#1e2840',
          borderRadius: isMobile ? '24px 24px 0 0' : 28,
          padding: isMobile ? '28px 20px 36px' : '36px 36px',
          width: isMobile ? '100%' : 440,
          maxHeight: isMobile ? '98vh' : 'auto',
          overflowY: 'auto',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
            {isEditing ? 'Modifier le contact' : 'Nouveau contact'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 12,
              padding: 8,
              color: '#94a3b8',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <div style={labelStyle}><User size={12} /> Prénom</div>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jean"
                style={inputStyle}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
                autoFocus
              />
            </div>
            <div>
              <div style={labelStyle}><User size={12} /> Nom</div>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Dupont"
                style={inputStyle}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
              />
            </div>
          </div>

          <div>
            <div style={labelStyle}><Phone size={12} /> Téléphone</div>
            <input
              type="tel"
              value={phone}
              onChange={(e) => {
                let raw = e.target.value;
                // Keep only digits, spaces, and + at start
                const hasPlus = raw.startsWith('+');
                const digits = raw.replace(/[^\d]/g, '');
                // If it's a 10-digit French number (0x xx xx xx xx), auto-format
                if (digits.length <= 10 && !hasPlus) {
                  const formatted = digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
                  setPhone(formatted);
                } else if (hasPlus) {
                  // +33 format: +33 6 12 34 56 78
                  const d = digits;
                  if (d.length <= 11) {
                    let f = '+' + d.slice(0, 2);
                    if (d.length > 2) f += ' ' + d.slice(2, 3);
                    if (d.length > 3) f += ' ' + d.slice(3).replace(/(\d{2})(?=\d)/g, '$1 ').trim();
                    setPhone(f);
                  } else {
                    setPhone(raw);
                  }
                } else {
                  setPhone(raw);
                }
              }}
              placeholder="xx xx xx xx xx"
              style={inputStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
            />
          </div>

          <div>
            <div style={labelStyle}><Mail size={12} /> Email</div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jean.dupont@email.com"
              style={inputStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
            />
          </div>

          {/* Labels */}
          <div>
            <div style={labelStyle}><Tag size={12} /> Tags</div>
            <div style={{
              ...inputStyle,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              padding: '8px 12px',
              minHeight: 48,
              alignItems: 'center',
              position: 'relative',
            }}>
              {labels.map((l) => (
                <span key={l} style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', background: 'rgba(168,85,247,0.15)',
                  border: '1px solid rgba(168,85,247,0.3)', borderRadius: 99,
                  fontSize: 12, color: '#c084fc', fontWeight: 600,
                }}>
                  {l}
                  <button
                    type="button"
                    onClick={() => setLabels(prev => prev.filter(x => x !== l))}
                    style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
              <input
                ref={labelInputRef}
                type="text"
                value={labelInput}
                onChange={(e) => { setLabelInput(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && labelInput.trim()) {
                    e.preventDefault();
                    const val = labelInput.trim().replace(/,$/, '');
                    if (val && !labels.includes(val)) {
                      setLabels(prev => [...prev, val]);
                    }
                    setLabelInput('');
                    setShowSuggestions(false);
                  }
                  if (e.key === 'Backspace' && !labelInput && labels.length > 0) {
                    setLabels(prev => prev.slice(0, -1));
                  }
                }}
                placeholder={labels.length === 0 ? 'Ex : Amis, Travail, VIP...' : 'Ajouter...'}
                style={{
                  background: 'none', border: 'none', outline: 'none',
                  color: '#f1f5f9', fontSize: 13, flex: 1, minWidth: 80,
                  padding: '4px 0', fontFamily: 'inherit',
                }}
              />
              {/* Suggestions dropdown */}
              {showSuggestions && labelInput.trim() && (() => {
                const suggestions = existingLabels.filter(
                  l => l.toLowerCase().includes(labelInput.toLowerCase()) && !labels.includes(l)
                );
                if (suggestions.length === 0) return null;
                return (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: '#1a2236', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12, marginTop: 4, maxHeight: 150, overflowY: 'auto',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                  }}>
                    {suggestions.map(s => (
                      <button
                        key={s}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setLabels(prev => [...prev, s]);
                          setLabelInput('');
                          setShowSuggestions(false);
                          labelInputRef.current?.focus();
                        }}
                        style={{
                          width: '100%', padding: '10px 16px', background: 'none',
                          border: 'none', color: '#c084fc', fontSize: 13, fontWeight: 600,
                          cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                          display: 'flex', alignItems: 'center', gap: 8,
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(168,85,247,0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <Tag size={11} /> {s}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              Appuyez sur Entrée ou virgule pour ajouter un tag
            </div>
          </div>

          <div>
            <div style={labelStyle}><AtSign size={12} /> Tag Instagram</div>
            <input
              type="text"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value.replace(/@/g, ''))}
              placeholder="jeandupont"
              style={inputStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(236,72,153,0.4)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
            />
          </div>

          <button
            type="submit"
            disabled={!canSave}
            style={{
              width: '100%',
              padding: '16px 24px',
              background: canSave ? '#2563eb' : 'rgba(37,99,235,0.3)',
              border: 'none',
              borderRadius: 16,
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: canSave ? 'pointer' : 'not-allowed',
              boxShadow: canSave ? '0 4px 20px rgba(37,99,235,0.3)' : 'none',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginTop: 6,
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => canSave && (e.currentTarget.style.background = '#3b82f6')}
            onMouseLeave={(e) => canSave && (e.currentTarget.style.background = '#2563eb')}
          >
            <UserPlus size={16} />
            {isEditing ? 'Enregistrer les modifications' : 'Ajouter le contact'}
          </button>
        </form>
      </div>
    </div>
  );
};


// ─── FolderModal ──────────────────────────────────────────────────────────────
const FolderModal = ({ onClose, onSave, groups, isMobile }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [members, setMembers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const handleAddEmail = () => {
    if (email && email.includes('@') && !members.includes(email)) {
      setMembers([...members, email]);
      setEmail('');
    }
  };

  const toggleGroup = (id) => setSelectedGroups((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 20,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          background: '#1e2840',
          border: '1px solid rgba(255,255,255,0.1)',
          width: '100%',
          maxWidth: isMobile ? '100%' : 520,
          borderRadius: isMobile ? '20px 20px 0 0' : 28,
          padding: isMobile ? '24px 16px' : '36px 32px',
          boxShadow: '0 40px 100px rgba(0,0,0,0.6)',
          maxHeight: isMobile ? '90vh' : undefined,
          overflowY: isMobile ? 'auto' : undefined,
        }}
        className="animate-in"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <h3 style={{ fontSize: 22, fontWeight: 800 }}>Créer un espace partagé</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 6 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
          >
            <X size={22} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nom du dossier</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Client XYZ"
              style={{
                width: '100%',
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 14,
                padding: '13px 16px',
                color: '#f1f5f9',
                fontSize: 14,
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)')}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Inviter via email</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                placeholder="email@gmail.com"
                type="email"
                style={{
                  flex: 1,
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14,
                  padding: '13px 16px',
                  color: '#f1f5f9',
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)')}
              />
              <button
                onClick={handleAddEmail}
                style={{
                  padding: '13px 16px',
                  background: '#fff',
                  color: '#000',
                  border: 'none',
                  borderRadius: 14,
                  cursor: 'pointer',
                  fontWeight: 700,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#e0e7ff')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
              >
                <Plus size={18} />
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {members.map((m) => (
                <span
                  key={m}
                  style={{
                    padding: '4px 12px',
                    background: 'rgba(59,130,246,0.1)',
                    border: '1px solid rgba(59,130,246,0.2)',
                    borderRadius: 10,
                    fontSize: 12,
                    color: '#60a5fa',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {m}
                  <X size={12} style={{ cursor: 'pointer' }} onClick={() => setMembers(members.filter((x) => x !== m))} />
                </span>
              ))}
            </div>
          </div>

          {groups.length > 0 && (
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lier des groupes</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {groups.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => toggleGroup(g.id)}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 12,
                      border: selectedGroups.includes(g.id) ? '1px solid rgba(99,102,241,0.5)' : '1px solid rgba(255,255,255,0.06)',
                      background: selectedGroups.includes(g.id) ? 'rgba(99,102,241,0.15)' : 'rgba(0,0,0,0.2)',
                      color: selectedGroups.includes(g.id) ? '#818cf8' : '#94a3b8',
                      fontSize: 13,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s',
                    }}
                  >
                    {g.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          disabled={!name}
          onClick={() => onSave({ name, members, groupIds: selectedGroups })}
          style={{
            width: '100%',
            marginTop: 28,
            padding: '15px 20px',
            background: name ? '#2563eb' : 'rgba(37,99,235,0.3)',
            border: 'none',
            borderRadius: 16,
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: name ? 'pointer' : 'not-allowed',
            boxShadow: name ? '0 4px 20px rgba(37,99,235,0.3)' : 'none',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => name && (e.currentTarget.style.background = '#3b82f6')}
          onMouseLeave={(e) => name && (e.currentTarget.style.background = '#2563eb')}
        >
          Créer le dossier et partager
        </button>
      </div>
    </div>
  );
};

// ─── ShareGroupModal ──────────────────────────────────────────────────────────
const ShareGroupModal = ({ onClose, onShare, groupName, isMobile }) => {
  const [email, setEmail] = useState('');

  const handleShare = () => {
    if (email.trim() && email.includes('@')) {
      onShare(email.trim());
      onClose();
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
      padding: isMobile ? 0 : 20, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
    }}>
      <div style={{
        background: '#1e2840', border: '1px solid rgba(255,255,255,0.1)',
        width: '100%', maxWidth: isMobile ? '100%' : 440,
        borderRadius: isMobile ? '20px 20px 0 0' : 28,
        padding: isMobile ? '24px 16px' : '36px 32px',
        boxShadow: '0 40px 100px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', gap: 24,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 20, fontWeight: 800 }}>Partager le groupe</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 6 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}>
            <X size={22} />
          </button>
        </div>

        <p style={{ fontSize: 13, color: '#7a8aa0', lineHeight: 1.5 }}>
          Partagez le groupe <strong>{groupName}</strong> avec un autre utilisateur en renseignant son adresse Gmail.
        </p>

        {/* Input */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Adresse Gmail du destinataire
          </label>
          <input
            autoFocus
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleShare()}
            placeholder="destinataire@gmail.com"
            style={{
              width: '100%', background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14,
              padding: '13px 16px', color: '#f1f5f9', fontSize: 14,
              outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
            }}
            onFocus={e => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)')}
            onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)')}
          />
        </div>

        {/* Submit */}
        <button
          disabled={!email.trim() || !email.includes('@')}
          onClick={handleShare}
          style={{
            width: '100%', padding: '15px 20px',
            background: (email.trim() && email.includes('@')) ? '#2563eb' : 'rgba(37,99,235,0.3)',
            border: 'none', borderRadius: 16, color: '#fff',
            fontSize: 14, fontWeight: 700,
            cursor: (email.trim() && email.includes('@')) ? 'pointer' : 'not-allowed',
            boxShadow: (email.trim() && email.includes('@')) ? '0 4px 20px rgba(37,99,235,0.3)' : 'none',
            transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
          onMouseEnter={e => (email.trim() && email.includes('@')) && (e.currentTarget.style.background = '#3b82f6')}
          onMouseLeave={e => (email.trim() && email.includes('@')) && (e.currentTarget.style.background = '#2563eb')}
        >
          <Share2 size={16} /> Partager
        </button>
      </div>
    </div>
  );
};

// ─── ProfileEditor ────────────────────────────────────────────────────────────
const ProfileEditor = ({ user, userProfile, isMobile, onSaveProfile }) => {
  const [phone, setPhone] = useState(userProfile.phone || '');
  const [instagram, setInstagram] = useState(userProfile.instagram || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Sync when profile loads from Firestore
  useEffect(() => {
    setPhone(userProfile.phone || '');
    setInstagram(userProfile.instagram || '');
  }, [userProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const nameParts = (user.displayName || '').trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      await onSaveProfile(user.uid, {
        phone, instagram,
        firstName, lastName,
        displayName: user.displayName || '',
        email: user.email || '',
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
    setSaving(false);
  };

  const inputStyle = {
    width: '100%', background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12,
    padding: '11px 14px', color: '#f1f5f9', fontSize: 13,
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6,
  };

  return (
    <div style={{
      background: 'rgba(30,40,64,0.6)', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 20, padding: isMobile ? 20 : 28,
    }}>
      <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
        <User size={16} /> Mon profil
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <div style={labelStyle}><Phone size={11} /> Téléphone</div>
          <input
            type="tel" value={phone}
            onChange={(e) => {
              const raw = e.target.value;
              const hasPlus = raw.startsWith('+');
              const digits = raw.replace(/[^\d]/g, '');
              if (digits.length <= 10 && !hasPlus) {
                setPhone(digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim());
              } else { setPhone(raw); }
            }}
            placeholder="xx xx xx xx xx" style={inputStyle}
            onFocus={e => e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)'}
            onBlur={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
          />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={labelStyle}><AtSign size={11} /> Instagram</div>
        <input
          type="text" value={instagram}
          onChange={e => setInstagram(e.target.value.replace(/@/g, ''))}
          placeholder="moncompte" style={inputStyle}
          onFocus={e => e.currentTarget.style.borderColor = 'rgba(236,72,153,0.4)'}
          onBlur={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
        />
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          width: '100%', padding: '13px 20px',
          background: saved ? '#059669' : '#2563eb',
          border: 'none', borderRadius: 14, color: '#fff',
          fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
          boxShadow: saved ? '0 4px 20px rgba(5,150,105,0.3)' : '0 4px 20px rgba(37,99,235,0.3)',
          transition: 'all 0.3s', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 8, fontFamily: 'inherit',
        }}
      >
        {saved ? <><Check size={15} /> Enregistré !</> : saving ? 'Enregistrement...' : <><Pencil size={14} /> Enregistrer le profil</>}
      </button>
    </div>
  );
};

// ─── ContactShareQRModal ─────────────────────────────────────────────────────
const ContactShareQRModal = ({ contact, onClose, isMobile }) => {
  const [QRCodeSVG, setQRCodeSVG] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    import('qrcode.react').then(m => setQRCodeSVG(() => m.QRCodeSVG));
  }, []);

  const nameParts = contact.name.trim().split(' ');
  const qrData = JSON.stringify({
    t: 'tcq',
    fn: nameParts[0] || '',
    ln: nameParts.slice(1).join(' ') || '',
    p: contact.phone || '',
    e: contact.email || '',
    ig: contact.instagram || '',
  });

  const handleCopyLink = async () => {
    const text = [
      contact.name,
      contact.phone && ('Tel: ' + contact.phone),
      contact.email && ('Email: ' + contact.email),
      contact.instagram && ('Instagram: @' + contact.instagram),
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Coordonnees du contact :', text);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 150,
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : 20,
        background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(12px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(145deg, #1a2236, #1e2840)',
          borderRadius: isMobile ? '24px 24px 0 0' : 28,
          padding: isMobile ? '28px 24px 40px' : '36px 36px',
          width: isMobile ? '100%' : 400,
          border: '1px solid rgba(99,102,241,0.2)',
          boxShadow: '0 40px 100px rgba(0,0,0,0.7)',
          position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Glow BG */}
        <div style={{
          position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)',
          width: 300, height: 300, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.1), transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
              Partager le contact
            </h3>
            <p style={{ fontSize: 12, color: '#7a8aa0', marginTop: 4 }}>
              Faites scanner ce QR par votre contact
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 12, padding: 8, color: '#94a3b8', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* QR Code */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          <div style={{
            padding: 20, background: '#fff', borderRadius: 20,
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}>
            {QRCodeSVG ? (
              <QRCodeSVG
                value={qrData}
                size={isMobile ? 200 : 220}
                level='M'
                bgColor='#ffffff'
                fgColor='#0f1623'
                style={{ display: 'block' }}
              />
            ) : (
              <div style={{ width: isMobile ? 200 : 220, height: isMobile ? 200 : 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 32, height: 32, border: '3px solid rgba(99,102,241,0.2)', borderTopColor: '#818cf8', borderRadius: '50%' }} className='animate-spin' />
              </div>
            )}
          </div>

          {/* Nom du contact */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{contact.name}</div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
              {contact.phone && (
                <span style={{ fontSize: 11, color: '#818cf8', background: 'rgba(99,102,241,0.1)', padding: '3px 8px', borderRadius: 6 }}>
                  📱 {contact.phone}
                </span>
              )}
              {contact.instagram && (
                <span style={{ fontSize: 11, color: '#ec4899', background: 'rgba(236,72,153,0.08)', padding: '3px 8px', borderRadius: 6 }}>
                  @{contact.instagram}
                </span>
              )}
              {contact.email && (
                <span style={{ fontSize: 11, color: '#94a3b8', background: 'rgba(255,255,255,0.05)', padding: '3px 8px', borderRadius: 6 }}>
                  ✉ {contact.email}
                </span>
              )}
            </div>
          </div>

          {/* Bouton copier les coordonnees */}
          <button
            onClick={handleCopyLink}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              background: copied ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.05)',
              border: copied ? '1px solid rgba(74,222,128,0.25)' : '1px solid rgba(255,255,255,0.08)',
              borderRadius: 14, padding: '12px 20px',
              color: copied ? '#4ade80' : '#94a3b8',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s', fontFamily: 'inherit',
            }}
            onMouseEnter={e => { if (!copied) { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}}
            onMouseLeave={e => { if (!copied) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#94a3b8'; }}}
          >
            {copied ? <><Check size={14} /> Copie !</> : <><Copy size={14} /> Copier les coordonnees</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ContactDetailModal ───────────────────────────────────────────────────────
const ContactDetailModal = ({ contact, onClose, onEdit, isMobile }) => {
  const [showShareQR, setShowShareQR] = useState(false);

  const initials = contact.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const infoRow = (icon, label, value, href) => {
    if (!value) return null;
    const content = (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 18px', background: 'rgba(0,0,0,0.2)',
        borderRadius: 14, border: '1px solid rgba(255,255,255,0.04)',
        transition: 'all 0.15s',
        cursor: href ? 'pointer' : 'default',
      }}
        onMouseEnter={(e) => href && (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
        onMouseLeave={(e) => href && (e.currentTarget.style.background = 'rgba(0,0,0,0.2)')}
      >
        <div style={{ padding: 8, borderRadius: 10, background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
        </div>
        {href && <ArrowRight size={14} color="#64748b" />}
      </div>
    );

    if (href) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          {content}
        </a>
      );
    }
    return content;
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : 20, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1e2840',
          borderRadius: isMobile ? '24px 24px 0 0' : 28,
          padding: isMobile ? '28px 20px 36px' : '36px',
          width: isMobile ? '100%' : 420,
          maxHeight: isMobile ? '90vh' : 'auto',
          overflowY: 'auto',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
        }}
      >
        {/* Close button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 12, padding: 8, color: '#94a3b8', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Avatar & Name */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          {contact.photo ? (
            <img src={contact.photo} alt={contact.name} style={{ width: 80, height: 80, borderRadius: 24, objectFit: 'cover', margin: '0 auto 16px', display: 'block', border: '3px solid rgba(99,102,241,0.3)' }} />
          ) : (
            <div style={{
              width: 80, height: 80, borderRadius: 24,
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, fontWeight: 800, color: '#fff',
              margin: '0 auto 16px', boxShadow: '0 8px 24px rgba(79,70,229,0.3)',
            }}>
              {initials}
            </div>
          )}
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', marginBottom: 4 }}>{contact.name}</h2>
          {contact.isShared && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', background: 'rgba(59,130,246,0.1)', padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(59,130,246,0.2)' }}>
              Partagé
            </span>
          )}
        </div>

        {/* Tags */}
        {contact.labels && contact.labels.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
            {contact.labels.map(l => (
              <span key={l} style={{
                padding: '5px 12px', background: 'rgba(168,85,247,0.1)',
                border: '1px solid rgba(168,85,247,0.25)', borderRadius: 99,
                fontSize: 12, fontWeight: 600, color: '#c084fc',
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <Tag size={10} /> {l}
              </span>
            ))}
          </div>
        )}

        {/* Info fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          {infoRow(<Phone size={16} color="#818cf8" />, 'Téléphone', contact.phone, contact.phone ? `tel:${contact.phone.replace(/\s/g, '')}` : null)}
          {infoRow(<Mail size={16} color="#818cf8" />, 'Email', contact.email, contact.email ? `mailto:${contact.email}` : null)}
          {infoRow(<AtSign size={16} color="#ec4899" />, 'Instagram', contact.instagram, contact.instagram ? `https://www.instagram.com/${contact.instagram.replace(/^@/, '')}` : null)}
        </div>

        {/* Boutons d'action */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {onEdit && !contact.isShared && (
            <button
              onClick={onEdit}
              style={{
                width: '100%', padding: '14px 24px',
                background: '#2563eb', border: 'none', borderRadius: 16,
                color: '#fff', fontSize: 14, fontWeight: 700,
                cursor: 'pointer', boxShadow: '0 4px 20px rgba(37,99,235,0.3)',
                transition: 'all 0.2s', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 8, fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#3b82f6'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
            >
              <Pencil size={15} /> Modifier le contact
            </button>
          )}
          {/* Bouton Partager : genere un QR code du contact pour qu'une autre personne puisse le scanner */}
          <button
            onClick={() => setShowShareQR(true)}
            style={{
              width: '100%', padding: '13px 24px',
              background: 'rgba(99,102,241,0.1)',
              border: '1px solid rgba(99,102,241,0.25)',
              borderRadius: 16, color: '#818cf8',
              fontSize: 14, fontWeight: 700,
              cursor: 'pointer', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8, fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.18)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.1)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.25)'; }}
          >
            <QrCode size={15} /> Partager via QR
          </button>
        </div>

        {/* Modal QR de partage du contact */}
        {showShareQR && (
          <ContactShareQRModal
            contact={contact}
            onClose={() => setShowShareQR(false)}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
};

// ─── LabelFilterDropdown ──────────────────────────────────────────────────────
const LabelFilterDropdown = ({ existingLabels, selectedLabelFilter, setSelectedLabelFilter }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const active = selectedLabelFilter !== 'all';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen(p => !p)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: active ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.06)',
            border: `1px solid ${active ? 'rgba(168,85,247,0.4)' : 'rgba(168,85,247,0.15)'}`,
            borderRadius: 12, padding: '8px 14px',
            color: active ? '#c084fc' : '#94a3b8',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            transition: 'all 0.2s', fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(168,85,247,0.5)'; e.currentTarget.style.background = 'rgba(168,85,247,0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = active ? 'rgba(168,85,247,0.4)' : 'rgba(168,85,247,0.15)'; e.currentTarget.style.background = active ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.06)'; }}
        >
          <Tag size={12} />
          {active ? selectedLabelFilter : 'Tous les tags'}
          <ChevronLeft size={12} style={{ transform: open ? 'rotate(90deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
        </button>

        {open && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50,
            background: '#1a2236', border: '1px solid rgba(168,85,247,0.2)',
            borderRadius: 14, padding: 6, minWidth: 200,
            boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(12px)',
          }}>
            <button
              onClick={() => { setSelectedLabelFilter('all'); setOpen(false); }}
              style={{
                width: '100%', padding: '10px 14px', background: !active ? 'rgba(168,85,247,0.12)' : 'none',
                border: 'none', borderRadius: 10, color: !active ? '#c084fc' : '#94a3b8',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(168,85,247,0.1)'; e.currentTarget.style.color = '#c084fc'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = !active ? 'rgba(168,85,247,0.12)' : 'none'; e.currentTarget.style.color = !active ? '#c084fc' : '#94a3b8'; }}
            >
              <Layers size={12} /> Tous les tags
              {!active && <Check size={12} style={{ marginLeft: 'auto' }} />}
            </button>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 8px' }} />
            {existingLabels.map(l => (
              <button
                key={l}
                onClick={() => { setSelectedLabelFilter(l); setOpen(false); }}
                style={{
                  width: '100%', padding: '10px 14px', background: selectedLabelFilter === l ? 'rgba(168,85,247,0.12)' : 'none',
                  border: 'none', borderRadius: 10, color: selectedLabelFilter === l ? '#c084fc' : '#cbd5e1',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(168,85,247,0.1)'; e.currentTarget.style.color = '#c084fc'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = selectedLabelFilter === l ? 'rgba(168,85,247,0.12)' : 'none'; e.currentTarget.style.color = selectedLabelFilter === l ? '#c084fc' : '#cbd5e1'; }}
              >
                <Tag size={11} /> {l}
                {selectedLabelFilter === l && <Check size={12} style={{ marginLeft: 'auto' }} />}
              </button>
            ))}
          </div>
        )}
      </div>

      {active && (
        <button
          onClick={() => setSelectedLabelFilter('all')}
          style={{
            background: 'none', border: 'none', color: '#ef4444',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '6px 10px', borderRadius: 8, transition: 'background 0.15s',
            fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
        >
          <X size={12} /> Réinitialiser
        </button>
      )}
    </div>
  );
};

// ProfileQRCode and QRScannerModal are lazy-loaded from ./components/ for code splitting

// ─── ScannedContactModal ──────────────────────────────────────────────────────
const ScannedContactModal = ({ contact, onClose, onAdd, isMobile }) => {
  const fullName = `${contact.fn || ''} ${contact.ln || ''}`.trim() || 'Contact';
  const initials = fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : 20, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1e2840',
          borderRadius: isMobile ? '24px 24px 0 0' : 28,
          padding: isMobile ? '28px 20px 36px' : '36px',
          width: isMobile ? '100%' : 420,
          border: '1px solid rgba(99,102,241,0.2)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
          animation: 'fade-in 0.3s ease forwards',
        }}
      >
        {/* Success badge */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <div style={{
            padding: 16, borderRadius: 20,
            background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.2)',
          }}>
            <Check size={32} color="#4ade80" />
          </div>
        </div>

        <h3 style={{
          fontSize: 20, fontWeight: 800, color: '#fff', textAlign: 'center',
          marginBottom: 4, letterSpacing: '-0.02em',
        }}>
          Contact trouvé !
        </h3>
        <p style={{ fontSize: 13, color: '#7a8aa0', textAlign: 'center', marginBottom: 24 }}>
          Voulez-vous ajouter ce contact à votre répertoire ?
        </p>

        {/* Contact preview */}
        <div style={{
          background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 16, padding: 18, marginBottom: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 16,
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, fontWeight: 800, color: '#fff', flexShrink: 0,
            }}>
              {initials}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{fullName}</div>
              {contact.e && <div style={{ fontSize: 12, color: '#7a8aa0' }}>{contact.e}</div>}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {contact.p && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#cbd5e1' }}>
                <Phone size={14} color="#818cf8" />
                {contact.p}
              </div>
            )}
            {contact.e && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#cbd5e1' }}>
                <Mail size={14} color="#818cf8" />
                {contact.e}
              </div>
            )}
            {contact.ig && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#cbd5e1' }}>
                <AtSign size={14} color="#ec4899" />
                {contact.ig}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={onAdd}
            style={{
              width: '100%', padding: '15px 24px',
              background: '#4f46e5', border: 'none', borderRadius: 16,
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 4px 20px rgba(79,70,229,0.3)',
              transition: 'all 0.2s', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8, fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#6366f1')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#4f46e5')}
          >
            <UserPlus size={16} /> Ajouter au répertoire
          </button>
          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '13px 24px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 16, color: '#94a3b8', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── DeleteAccountModal ───────────────────────────────────────────────────────
const DeleteAccountModal = ({ onClose, onConfirm, isMobile }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const isValid = confirmText === 'SUPPRIMER';

  const handleConfirm = async () => {
    if (!isValid) return;
    setIsDeleting(true);
    await onConfirm();
    setIsDeleting(false);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
        padding: isMobile ? 0 : 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1a1f2e',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: isMobile ? '24px 24px 0 0' : 24,
          padding: isMobile ? '28px 20px 36px' : 36,
          width: isMobile ? '100%' : 420,
          boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
        }}
      >
        {/* Icône danger */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 18,
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Trash2 size={26} color="#ef4444" />
          </div>
        </div>

        <h3 style={{ fontSize: 20, fontWeight: 800, color: '#fff', textAlign: 'center', marginBottom: 8, letterSpacing: '-0.02em' }}>
          Supprimer mon compte
        </h3>
        <p style={{ fontSize: 13, color: '#7a8aa0', textAlign: 'center', marginBottom: 24, lineHeight: 1.6 }}>
          Cette action est <strong style={{ color: '#fca5a5' }}>irréversible</strong>. Tous vos contacts, groupes et données seront définitivement supprimés.
        </p>

        {/* Ce qui sera supprimé */}
        <div style={{
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)',
          borderRadius: 14, padding: '14px 16px', marginBottom: 24,
        }}>
          {['Tous vos contacts', 'Vos groupes', 'Votre profil'].map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, color: '#f87171' }}>
              <X size={12} /> {item}
            </div>
          ))}
        </div>

        {/* Confirmation par frappe */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 8, fontWeight: 600 }}>
            Tapez <span style={{ color: '#f87171', fontFamily: 'monospace' }}>SUPPRIMER</span> pour confirmer
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="SUPPRIMER"
            autoFocus
            style={{
              width: '100%',
              background: 'rgba(0,0,0,0.3)',
              border: `1px solid ${isValid ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 12, padding: '12px 16px',
              color: '#fff', fontSize: 14, fontFamily: 'monospace',
              outline: 'none', transition: 'border-color 0.2s',
            }}
          />
        </div>

        {/* Boutons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={handleConfirm}
            disabled={!isValid || isDeleting}
            style={{
              width: '100%', padding: '14px',
              background: isValid ? '#dc2626' : 'rgba(239,68,68,0.2)',
              border: 'none', borderRadius: 14,
              color: isValid ? '#fff' : '#7a8aa0',
              fontSize: 14, fontWeight: 700,
              cursor: isValid && !isDeleting ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {isDeleting ? 'Suppression...' : <><Trash2 size={14} /> Supprimer définitivement</>}
          </button>
          <button
            onClick={onClose}
            disabled={isDeleting}
            style={{
              width: '100%', padding: '13px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 14, color: '#94a3b8',
              fontSize: 14, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── CameraPermissionModal ────────────────────────────────────────────────────
const CameraPermissionModal = ({ onClose, onConfirm, isMobile }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
      padding: isMobile ? 0 : 20,
    }}
    onClick={onClose}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: '#1e2840',
        border: '1px solid rgba(99,102,241,0.2)',
        borderRadius: isMobile ? '24px 24px 0 0' : 24,
        padding: isMobile ? '32px 24px calc(32px + env(safe-area-inset-bottom, 0px))' : '36px',
        width: isMobile ? '100%' : 420,
        boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
        animation: 'fade-in 0.25s ease forwards',
      }}
    >
      {/* Icon */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
        <div style={{
          width: 72, height: 72, borderRadius: 22,
          background: 'rgba(99,102,241,0.12)',
          border: '1px solid rgba(99,102,241,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <QrCode size={34} color="#818cf8" />
        </div>
      </div>

      <h3 style={{
        fontSize: 20, fontWeight: 800, color: '#fff',
        textAlign: 'center', marginBottom: 10, letterSpacing: '-0.02em',
      }}>
        Accès à la caméra
      </h3>
      <p style={{
        fontSize: 14, color: '#94a3b8', textAlign: 'center',
        marginBottom: 28, lineHeight: 1.6,
      }}>
        Pour scanner un QR code TuConnaisQui, l'application a besoin d'accéder à votre caméra.
      </p>

      {/* Info box */}
      <div style={{
        background: 'rgba(99,102,241,0.07)',
        border: '1px solid rgba(99,102,241,0.15)',
        borderRadius: 14, padding: '12px 16px', marginBottom: 24,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <div style={{
          width: 22, height: 22, borderRadius: 8, flexShrink: 0,
          background: 'rgba(99,102,241,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginTop: 1,
        }}>
          <ShieldCheck size={13} color="#818cf8" />
        </div>
        <p style={{ fontSize: 12, color: '#7a8aa0', lineHeight: 1.6, margin: 0 }}>
          La caméra n'est utilisée que pendant le scan. Aucune photo n'est prise ni sauvegardée.
        </p>
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          onClick={onConfirm}
          style={{
            width: '100%', padding: '15px 24px',
            background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
            border: 'none', borderRadius: 16, color: '#fff',
            fontSize: 15, fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(79,70,229,0.35)',
            transition: 'all 0.2s', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          <QrCode size={17} /> Autoriser et scanner
        </button>
        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '13px 24px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 16, color: '#94a3b8',
            fontSize: 14, fontWeight: 600, cursor: 'pointer',
            transition: 'all 0.2s', fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#94a3b8'; }}
        >
          Annuler
        </button>
      </div>
    </div>
  </div>
);


// ─── SharedProfilePage ────────────────────────────────────────────────────────
const SharedProfilePage = ({ profile, isLoading, isLoggedIn, onAdd, onDecline, isMobile }) => {
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  const initials = profile
    ? `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`.toUpperCase() || '?'
    : '?';

  const fullName = profile
    ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.email || 'Contact'
    : '';

  const handleAdd = async () => {
    if (!isLoggedIn) {
      alert('Connectez-vous pour ajouter ce contact à votre répertoire.');
      return;
    }
    setAdding(true);
    await onAdd();
    setAdded(true);
    setAdding(false);
    setTimeout(() => onDecline(), 1200);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(16px)',
        padding: isMobile ? 16 : 20,
      }}
    >
      <div
        style={{
          background: 'linear-gradient(145deg, #1a2236, #1e2840)',
          border: '1px solid rgba(99,102,241,0.2)',
          borderRadius: isMobile ? 24 : 28,
          padding: isMobile ? '32px 24px' : '48px 40px',
          width: '100%',
          maxWidth: 460,
          boxShadow: '0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(99,102,241,0.1)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* BG glow */}
        <div style={{
          position: 'absolute', top: -60, left: '50%', transform: 'translateX(-50%)',
          width: 320, height: 320, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.12), transparent 70%)',
          pointerEvents: 'none',
        }} />

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{
              width: 44, height: 44, border: '3px solid rgba(99,102,241,0.2)',
              borderTopColor: '#818cf8', borderRadius: '50%',
              margin: '0 auto 16px',
            }} className="animate-spin" />
            <p style={{ color: '#7a8aa0', fontSize: 14 }}>Chargement du profil...</p>
          </div>
        ) : added ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 20,
              background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <Check size={32} color="#4ade80" />
            </div>
            <p style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>Contact ajoute !</p>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{
                width: 96, height: 96, borderRadius: 28,
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 34, fontWeight: 800, color: '#fff',
                margin: '0 auto 20px',
                boxShadow: '0 12px 32px rgba(79,70,229,0.35)',
              }}>
                {initials}
              </div>
              <h2 style={{
                fontSize: isMobile ? 22 : 26, fontWeight: 800, color: '#fff',
                letterSpacing: '-0.03em', marginBottom: 6,
              }}>
                {fullName}
              </h2>
              {profile?.email && (
                <p style={{ fontSize: 13, color: '#7a8aa0' }}>{profile.email}</p>
              )}
            </div>

            {(profile?.phone || profile?.instagram) && (
              <div style={{
                background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 16, padding: '16px 20px', marginBottom: 28,
                display: 'flex', flexDirection: 'column', gap: 12,
              }}>
                {profile?.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Phone size={14} color="#818cf8" />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 1 }}>Telephone</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>{profile.phone}</div>
                    </div>
                  </div>
                )}
                {profile?.instagram && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(236,72,153,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <AtSign size={14} color="#ec4899" />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 1 }}>Instagram</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>@{profile.instagram}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <p style={{ fontSize: 13, color: '#7a8aa0', textAlign: 'center', marginBottom: 24, lineHeight: 1.6 }}>
              <strong style={{ color: '#c7d2fe' }}>{fullName}</strong> vous a partage son profil TuConnaisQui.
              Voulez-vous l ajouter a votre repertoire ?
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={handleAdd}
                disabled={adding}
                style={{
                  width: '100%', padding: '16px 24px',
                  background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
                  border: 'none', borderRadius: 16, color: '#fff',
                  fontSize: 15, fontWeight: 700, cursor: adding ? 'wait' : 'pointer',
                  boxShadow: '0 4px 20px rgba(79,70,229,0.35)',
                  transition: 'all 0.2s', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 8, fontFamily: 'inherit',
                  opacity: adding ? 0.7 : 1,
                }}
                onMouseEnter={(e) => { if (!adding) e.currentTarget.style.opacity = '0.9'; }}
                onMouseLeave={(e) => { if (!adding) e.currentTarget.style.opacity = '1'; }}
              >
                <UserPlus size={18} />
                {adding ? 'Ajout en cours...' : 'Ajouter le contact'}
              </button>
              <button
                onClick={onDecline}
                disabled={adding}
                style={{
                  width: '100%', padding: '14px 24px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 16, color: '#94a3b8',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  transition: 'all 0.2s', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#94a3b8'; }}
              >
                <X size={14} />
                Ne pas ajouter
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default App;
